import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { Send, PanelLeftClose, FolderOpen, ArrowDown, RotateCcw, X, FileText, FileDown, Loader2, Search, PenLine, HelpCircle, BookOpen, Check, FileBadge, GitCompare, Workflow as WorkflowIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import { useAutoScroll } from '../../hooks/use-auto-scroll';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '../ui/resizable';
import { useSidebar } from '../ui/sidebar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Label } from '../ui/label';
import { useBoundStore } from '../../store';
import {
  getQAgentChat,
  getQAgentMessages,
  streamQAgentMessage,
  attachProjectToChat,
  getQAgentSuggestions,
  updateSuggestionStatus,
  acceptAllSuggestions,
  rejectAllSuggestions,
  undoSuggestion as undoSuggestionApi,
  QAgentMessage,
  QAgentCitation,
  QAgentDocumentNotice,
  QAgentSuggestion,
  StreamQAgentMessageDto,
  clearQAgentChatContext,
  exportFileWithChanges,
  ExportFileOptions,
  linkContractToChat,
  exportContractAsProjectFile,
  addComparisonCard,
} from '../../services/QAgent';
import {
  getProjectFiles,
  getProjectPlaybooks,
  linkPlaybook,
  linkAllPlaybooks,
  unlinkPlaybook,
  deleteProjectFile,
  ProjectFile,
  ProjectPlaybook,
} from '../../services/Project';
import { listStandardAgreements, StandardAgreement } from '../../services/StandardAgreements';
import { getComparison, startComparison } from '../../services/Comparisons';
import ComparisonCard from '../comparisons/ComparisonCard';
import CompareFileDialog from '../comparisons/CompareFileDialog';
import { fillWorkflow, listWorkflows, Workflow } from '../../services/Workflows';
import { QAgentChatMessage, stripToolResults } from './QAgentChatMessage';
import type { ExportEntry } from '@/lib/export-docx';
import { ContextPanel } from './ContextPanel';
import { MentionMenu } from './MentionMenu';
import { MentionInput, type Mention, type MentionInputHandle } from './MentionInput';
import { FileDropZone } from './FileDropZone';
import { FileViewerSheet } from './FileViewerSheet';
import { GenerateContractDialog } from './GenerateContractDialog';
import { ProjectUploadDialog } from './ProjectUploadDialog';
import { PROJECT_FILE_ACCEPT } from './project-uploads';
import { useProjectFileUploads } from './useProjectFileUploads';
import { PlaybookViewerSheet } from './PlaybookViewerSheet';
import { SuggestionCard, defaultExportOptions } from './suggestions';
import { SlashCommandMenu, SLASH_COMMANDS, type SlashCommand } from './slash-commands';
import queriIcon from '../../assets/queri-icon.png';
import { ContractFormMessage } from './ContractFormMessage';
import { ContractFailedMessage } from './ContractFailedMessage';
import { ContractLivePreview } from './ContractLivePreview';
import { ContractSectionsMessage, type ContractSaveStatus } from './ContractSectionsMessage';
import {
  ContractStage,
  ContractJobStatus,
  submitContractState,
  waitForContractGeneration,
  getContractJobStatus,
  getContract,
  type ContractState,
  type ContractPartyField,
  type ContractSection,
  type ContractSubSection,
} from '../../services/Contract';
import { toast } from 'sonner';
import { Hint } from '../../onboarding/Hint';
import { useI18n } from '../../i18n';
import { startDownload } from '../../utils/download';

export const QAgentChat: React.FC = () => {
  const { t, locale } = useI18n();
  const { chatId } = useParams<{ chatId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { open: isSidebarOpen, setOpen: setSidebarOpen } = useSidebar();

  // Track if sidebar was open before context panel was opened
  const sidebarWasOpenRef = useRef<boolean>(true);

  // Chat state
  const [messages, setMessages] = useState<QAgentMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(true);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [activeToolStatus, setActiveToolStatus] = useState<string | null>(null);
  const [activeSkillId, setActiveSkillId] = useState<string | null>(null);
  const [generationProgress, setGenerationProgress] = useState<number | null>(null);
  // The contract so far while its sections are being written
  const [liveContract, setLiveContract] = useState<{ documentType: string; sections: ContractSection[] } | null>(null);
  const [queuedMessages, setQueuedMessages] = useState<QAgentMessage[]>([]);
  const [failedMessage, setFailedMessage] = useState<string | null>(null);
  const [messageText, setMessageText] = useState('');
  const [chatTitle, setChatTitle] = useState<string | null>(null);
  const [hasStartedChat, setHasStartedChat] = useState(false);

  // Project context state
  const [projectId, setProjectId] = useState<string | null>(null);
  const [projectName, setProjectName] = useState<string | null>(null);
  const [projectFiles, setProjectFiles] = useState<ProjectFile[]>([]);
  const projectFilesRef = useRef(projectFiles);
  projectFilesRef.current = projectFiles;
  const [projectPlaybooks, setProjectPlaybooks] = useState<ProjectPlaybook[]>([]);
  const [activeFileIds, setActiveFileIds] = useState<string[]>([]);
  const projectIdRef = useRef(projectId);
  projectIdRef.current = projectId;
  const [activePlaybookId, setActivePlaybookId] = useState<string | null>(null);
  const [loadingProjectData, setLoadingProjectData] = useState(false);
  const [isContextPanelOpen, setIsContextPanelOpen] = useState(false);

  // Context panel section expansion state (persists across sidebar toggles)
  const [filesExpanded, setFilesExpanded] = useState(true);
  const [playbooksExpanded, setPlaybooksExpanded] = useState(true);

  // File upload state
  const uploads = useProjectFileUploads();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Link playbook dialog state
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [selectedPlaybookToLink, setSelectedPlaybookToLink] = useState('');
  const [isLinking, setIsLinking] = useState(false);

  // Delete file dialog state
  const [fileToDelete, setFileToDelete] = useState<ProjectFile | null>(null);
  const [isDeletingFile, setIsDeletingFile] = useState(false);

  // Unlink playbook dialog state
  const [playbookToUnlink, setPlaybookToUnlink] = useState<ProjectPlaybook | null>(null);
  const [isUnlinkingPlaybook, setIsUnlinkingPlaybook] = useState(false);

  // File viewer state
  const [fileToView, setFileToView] = useState<ProjectFile | null>(null);
  const [hoveredRule, setHoveredRule] = useState<{ messageId: string; ruleId: string } | null>(null);
  const [selectedRule, setSelectedRule] = useState<{ messageId: string; ruleId: string } | null>(null);
  const [scrollToRuleQuote, setScrollToRuleQuote] = useState<string | null>(null);
  const [scrollToSuggestionId, setScrollToSuggestionId] = useState<string | null>(null);
  const fileViewerAnimatedRef = useRef(false);

  // Playbook viewer state
  const [playbookToView, setPlaybookToView] = useState<ProjectPlaybook | null>(null);
  const playbookViewerAnimatedRef = useRef(false);

  // Suggestions state
  const [suggestions, setSuggestions] = useState<QAgentSuggestion[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [suggestionsExpanded, setSuggestionsExpanded] = useState(true);
  // Tracks the file currently being exported with its suggested edits
  const [exportingFileId, setExportingFileId] = useState<string | null>(null);
  // Track which message suggestions should appear after
  const [suggestionsAnchorMessageId, setSuggestionsAnchorMessageId] = useState<string | null>(null);

  // Mention menu state
  const [isMentionMenuOpen, setIsMentionMenuOpen] = useState(false);
  const [mentionMenuPosition, setMentionMenuPosition] = useState({ top: 0, left: 0 });
  const [mentions, setMentions] = useState<Mention[]>([]);
  const mentionsRef = useRef<Mention[]>([]);
  const messageTextRef = useRef<string>('');

  // Slash command state
  const [isSlashMenuOpen, setIsSlashMenuOpen] = useState(false);
  const [slashFilter, setSlashFilter] = useState('');
  const [pendingSkillAction, setPendingSkillAction] = useState<string | null>(null);
  const [reviewSelectedFileId, setReviewSelectedFileId] = useState<string | null>(null);
  const [compareSelectedFileIds, setCompareSelectedFileIds] = useState<string[]>([]);
  // Compare against one of the organization's standard agreements
  const [standardAgreements, setStandardAgreements] = useState<StandardAgreement[]>([]);
  const [compareStandardId, setCompareStandardId] = useState<string | null>(null);
  const [isPreparingCompare, setIsPreparingCompare] = useState(false);
  // A project file being compared with a standard (dialog from the file list)
  const [compareFileTarget, setCompareFileTarget] = useState<ProjectFile | null>(null);
  // Set when this chat continues a comparison ("Continue in chat")
  const [chatComparisonId, setChatComparisonId] = useState<string | null>(null);
  const [reviewSelectedPlaybookId, setReviewSelectedPlaybookId] = useState<string | null>(null);
  // Saved workflows (Playbooks -> Workflows) appear as extra / commands
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const activeWorkflowRef = useRef<Workflow | null>(null);
  const [reviewPickerIndex, setReviewPickerIndex] = useState(0);
  const reviewPickerIndexRef = useRef(0);

  // Contract generation state
  const [isGeneratePickerOpen, setIsGeneratePickerOpen] = useState(false);
  const [generateDocType, setGenerateDocType] = useState('');
  const [generateDescription, setGenerateDescription] = useState('');
  const [activeContractId, setActiveContractId] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const mentionInputRef = useRef<MentionInputHandle>(null);
  const pendingSkillRef = useRef<string | null>(null);
  const streamingContentRef = useRef<string>('');
  const abortControllerRef = useRef<AbortController | null>(null);
  const messageQueueRef = useRef<string[]>([]);
  const pendingCitationsRef = useRef<QAgentCitation[]>([]);
  const pendingNoticesRef = useRef<QAgentDocumentNotice[]>([]);
  const initialMessageSent = useRef(false);
  const startGenerateRef = useRef<(overrides?: { docType?: string; description?: string; playbookId?: string | null }) => void>();
  const resumeGenerationRef = useRef<(contractId: string) => void>();
  // Aborted when leaving the chat, to stop polling a contract generation.
  // The backend job keeps running and is picked up again on return.
  const generationAbortRef = useRef<AbortController | null>(null);
  const chatIdRef = useRef(chatId);
  chatIdRef.current = chatId;
  const applySkillRef = useRef<(command: SlashCommand) => void>();

  const user = useBoundStore((state) => state.user);
  const projects = useBoundStore((state) => state.projects);
  const projectsRef = useRef(projects);
  projectsRef.current = projects;
  const projectAccess = projects.find(p => p.id === projectId)?.access;
  const fetchProjects = useBoundStore((state) => state.fetchProjects);
  const playbooks = useBoundStore((state) => state.playbooks);
  const fetchPlaybooks = useBoundStore((state) => state.fetchPlaybooks);
  const updateQAgentChatTitle = useBoundStore((state) => state.updateQAgentChatTitle);
  const updateQAgentChatProject = useBoundStore((state) => state.updateQAgentChatProject);

  // Auto-scroll hook for better scroll behavior
  const {
    containerRef: messagesContainerRef,
    scrollToBottom,
    handleScroll,
    shouldAutoScroll,
    handleTouchStart,
  } = useAutoScroll([messages, streamingContent, queuedMessages]);

  // Fetch suggestions for this chat
  const fetchSuggestions = useCallback(async () => {
    if (!chatId) return;
    setLoadingSuggestions(true);
    try {
      const response = await getQAgentSuggestions(chatId);
      if (response.success && response.data) {
        // Filter out exported suggestions - they're historical and shouldn't appear in the UI
        setSuggestions(response.data.filter(s => !s.exportedAt));
      }
    } catch (error) {
      console.error('[QAgentChat] Failed to fetch suggestions:', error);
    } finally {
      setLoadingSuggestions(false);
    }
  }, [chatId]);

  // Fetch projects for selector (always fetch fresh data on mount)
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Fetch playbooks when link dialog opens
  useEffect(() => {
    if (isLinkDialogOpen && playbooks.length === 0) {
      fetchPlaybooks();
    }
  }, [isLinkDialogOpen, playbooks.length, fetchPlaybooks]);

  // --- Contract generation messages -------------------------------------
  // These are client-side messages (not in the chat history), rebuilt from
  // the contract state when the chat is reopened.

  const buildGeneratePromptText = (docType: string, description?: string) =>
    description && description !== docType
      ? t.qagentChat.contract.generateWithDescription(docType, description)
      : t.qagentChat.contract.generatePrefix(docType);

  const buildContractFormMessage = (state: ContractState, idSuffix: string): QAgentMessage => {
    const formMessage: QAgentMessage = {
      id: `contract-form-${idSuffix}`,
      content: '__CONTRACT_FORM__',
      type: 'ai',
      createdAt: new Date().toISOString(),
    };
    // Store form data on the message object for rendering
    (formMessage as any)._contractForm = {
      parties: state.parties || [],
      otherInfo: state.otherUserInformation || [],
      contractId: state.contractId,
      state,
    };
    return formMessage;
  };

  const buildContractSectionsMessage = (
    state: ContractState,
    idSuffix: string,
    extra: {
      saveStatus?: ContractSaveStatus;
      file?: ProjectFile;
      projectName?: string | null;
    } = {},
  ): QAgentMessage => {
    const sectionsMessage: QAgentMessage = {
      id: `contract-sections-${idSuffix}`,
      content: '__CONTRACT_SECTIONS__',
      type: 'ai',
      createdAt: new Date().toISOString(),
    };
    Object.assign(sectionsMessage, {
      _contractSections: {
        sections: state.sections || [],
        documentType: state.documentType,
        playbookId: state.playbookId,
      },
      _saveStatus: extra.saveStatus,
      _contractFile: extra.file,
      _projectName: extra.projectName,
    });
    return sectionsMessage;
  };

  const buildContractFailedMessage = (contractId: string, idSuffix: string): QAgentMessage => {
    const failedMessage: QAgentMessage = {
      id: `contract-failed-${idSuffix}`,
      content: '__CONTRACT_FAILED__',
      type: 'ai',
      createdAt: new Date().toISOString(),
    };
    (failedMessage as any)._contractFailed = { contractId };
    return failedMessage;
  };

  const patchMessage = (messageId: string, patch: Record<string, unknown>) => {
    setMessages(prev => prev.map(m =>
      m.id === messageId ? { ...m, ...patch } as QAgentMessage : m
    ));
  };

  /**
   * Rebuild the contract generation UI for a chat with a linked contract:
   * the details form if it wasn't submitted yet, the result card if it's
   * done, or resume progress polling if it's still generating.
   */
  const hydrateContract = async (
    chat: { id: string; contractId: string | null; contractFileId: string | null; projectId: string | null },
    isActive: () => boolean,
  ) => {
    const contractId = chat.contractId;
    if (!contractId) return;
    setActiveContractId(contractId);

    const [contractResponse, statusResponse] = await Promise.all([
      getContract(contractId),
      getContractJobStatus(contractId),
    ]);
    if (!isActive() || !contractResponse.success || !contractResponse.data) return;

    const state = contractResponse.data;
    const jobStatus = statusResponse.data?.jobStatus;
    const isProcessing = jobStatus === ContractJobStatus.PROCESSING;
    const now = Date.now();
    const hydrated: QAgentMessage[] = [{
      id: `contract-prompt-${now}`,
      content: buildGeneratePromptText(state.documentType, state.documentDescription),
      type: 'human',
      createdAt: new Date().toISOString(),
    }];
    let shouldResume = false;

    if (state.stage <= ContractStage.INFO && !isProcessing) {
      // Details not submitted yet (or processing them failed): show the form again
      hydrated.push(buildContractFormMessage(state, `hydrated-${now}`));
    } else {
      // Submitted: the form as a summary of the values used
      const submittedForm = buildContractFormMessage(state, `hydrated-${now}`);
      (submittedForm as any)._contractForm.submitted = true;
      hydrated.push(submittedForm);

      if (state.stage >= ContractStage.REGENERATE && state.sections?.length) {
        // Generated: find the saved project file, if any
        let file: ProjectFile | undefined;
        if (chat.contractFileId && chat.projectId) {
          const filesResponse = await getProjectFiles(chat.projectId);
          if (!isActive()) return;
          file = filesResponse.data?.find(f => f.id === chat.contractFileId);
        }
        const savedProjectName = file
          ? projectsRef.current.find(p => p.id === file!.projectId)?.name ?? null
          : null;
        hydrated.push(buildContractSectionsMessage(state, `hydrated-${now}`, {
          saveStatus: file ? 'saved' : 'unsaved',
          file,
          projectName: savedProjectName,
        }));
      } else if (isProcessing || jobStatus === ContractJobStatus.COMPLETED || !statusResponse.success) {
        // Still generating (possibly between the INFO and SECTIONS jobs)
        shouldResume = true;
      } else {
        hydrated.push(buildContractFailedMessage(contractId, `hydrated-${now}`));
      }
    }

    setHasStartedChat(true);
    setMessages(prev => [...prev, ...hydrated]);
    if (shouldResume) {
      resumeGenerationRef.current?.(contractId);
    }
  };

  // Redirect if no chatId
  useEffect(() => {
    if (!chatId) {
      console.warn('[QAgentChat] No chatId in URL, redirecting to home');
      navigate('/');
    }
  }, [chatId, navigate]);

  // Load chat and messages
  useEffect(() => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!chatId || !uuidRegex.test(chatId)) {
      console.warn('[QAgentChat] Invalid chatId:', chatId);
      return;
    }

    let isMounted = true;

    // The component is reused across chats, so drop the previous chat's state
    setMessages([]);
    setHasStartedChat(false);
    setIsStreaming(false);
    setStreamingContent('');
    streamingContentRef.current = '';
    setActiveToolStatus(null);
    setGenerationProgress(null);
    setIsGenerating(false);
    setActiveContractId(null);
    setFileToView(null);
    setChatComparisonId(null);
    setSuggestionsAnchorMessageId(null);
    setFailedMessage(null);
    setQueuedMessages([]);
    messageQueueRef.current = [];
    initialMessageSent.current = false;
    // Files and playbook picked in the previous chat (e.g. by /review) don't
    // carry over; with no files the server uses all of the project's files
    setActiveFileIds([]);
    setActivePlaybookId(null);

    const loadChat = async () => {
      setLoadingMessages(true);
      try {
        // Get chat details
        const chatResponse = await getQAgentChat(chatId);
        if (!isMounted) return;
        if (chatResponse.success && chatResponse.data) {
          setChatTitle(chatResponse.data.title);
          setChatComparisonId(chatResponse.data.comparisonId ?? null);
          if (chatResponse.data.comparisonId) setHasStartedChat(true);
          const chatProjectId = chatResponse.data.projectId;
          // Same project: its files aren't reloaded, so select them again
          if (chatProjectId && chatProjectId === projectIdRef.current) {
            setActiveFileIds(projectFilesRef.current.filter(f => f.status === 'ready').map(f => f.id));
          }
          setProjectId(chatProjectId);
          const project = projectsRef.current.find(p => p.id === chatProjectId);
          // Email agent documents live in an internal container, shown as attachments
          setProjectName(
            chatResponse.data.projectHidden
              ? t.qagentChat.projectIndicator.emailAttachments
              : project?.name || null,
          );
        }

        // Get messages
        const messagesResponse = await getQAgentMessages(chatId);
        if (!isMounted) return;
        if (messagesResponse.success && messagesResponse.data) {
          setMessages(messagesResponse.data);
          if (messagesResponse.data.length > 0) {
            setHasStartedChat(true);
            // Anchor suggestions to the last AI message
            const lastAiMsg = [...messagesResponse.data].reverse().find(m => m.type === 'ai' && !m.comparisonId);
            if (lastAiMsg) {
              setSuggestionsAnchorMessageId(lastAiMsg.id);
            }
          }
        }

        // Restore contract generation if a contract is linked to this chat
        if (chatResponse.success && chatResponse.data?.contractId) {
          try {
            await hydrateContract(chatResponse.data, () => isMounted);
          } catch (err) {
            console.error('[QAgentChat] Failed to hydrate contract:', err);
          }
          if (!isMounted) return;
        }

        // Get suggestions
        await fetchSuggestions();
      } catch (error) {
        if (!isMounted) return;
        toast.error(t.qagentChat.toasts.loadChatFailed);
      } finally {
        if (isMounted) {
          setLoadingMessages(false);
          setTimeout(() => mentionInputRef.current?.focus(), 100);
        }
      }
    };

    loadChat();
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, fetchSuggestions]);

  // Abort streaming and contract polling on unmount or chatId change
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
      generationAbortRef.current?.abort();
      generationAbortRef.current = null;
    };
  }, [chatId]);

  // Load project files and playbooks when projectId changes
  useEffect(() => {
    if (!projectId) {
      setProjectFiles([]);
      setProjectPlaybooks([]);
      setActiveFileIds([]);
      setActivePlaybookId(null);
      return;
    }

    const loadProjectData = async () => {
      setLoadingProjectData(true);
      try {
        const [filesResponse, playbooksResponse] = await Promise.all([
          getProjectFiles(projectId),
          getProjectPlaybooks(projectId),
        ]);

        if (filesResponse.success && filesResponse.data) {
          setProjectFiles(filesResponse.data);
          // Auto-select all ready files
          setActiveFileIds(
            filesResponse.data
              .filter(f => f.status === 'ready')
              .map(f => f.id)
          );
        }

        if (playbooksResponse.success && playbooksResponse.data) {
          setProjectPlaybooks(playbooksResponse.data);
        }
      } catch (error) {
        console.error('[QAgentChat] Failed to load project data:', error);
      } finally {
        setLoadingProjectData(false);
      }
    };

    loadProjectData();
  }, [projectId]);

  // A comparison chat works on the received agreement: show it, so the
  // suggestions land in view (on wide screens; on a phone it would cover the chat)
  useEffect(() => {
    if (!chatComparisonId || projectFiles.length === 0) return;
    if (!window.matchMedia('(min-width: 1024px)').matches) return;
    let cancelled = false;
    void getComparison(chatComparisonId).then(res => {
      const file = res.success && res.data ? projectFiles.find(f => f.id === res.data!.receivedFileId) : undefined;
      if (!cancelled && file) setFileToView(prev => prev ?? file);
    });
    return () => { cancelled = true; };
  }, [chatComparisonId, projectFiles]);

  // Handle initial message from navigation state
  useEffect(() => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!chatId || !uuidRegex.test(chatId) || initialMessageSent.current) return;

    const state = location.state as {
      initialMessage?: string;
      initialSkillId?: string;
      openGenerate?: boolean;
      generateDocType?: string;
      generateDescription?: string;
      generatePlaybookId?: string | null;
    } | null;

    if (state?.initialMessage && !loadingMessages && messages.length === 0) {
      const messageToSend = state.initialMessage;
      initialMessageSent.current = true;
      window.history.replaceState({}, '', location.pathname);

      setTimeout(() => {
        sendMessageRef.current?.(messageToSend);
      }, 150);
    }

    // Launched from the home composer with a slash skill (e.g. /review): enter
    // that skill so the user can attach a document.
    if (state?.initialSkillId && !loadingMessages && messages.length === 0) {
      initialMessageSent.current = true;
      window.history.replaceState({}, '', location.pathname);
      const command = SLASH_COMMANDS.find(
        (c) => c.skillId === state.initialSkillId,
      );
      if (command) {
        setTimeout(() => applySkillRef.current?.(command), 100);
      }
    }

    if (state?.openGenerate && !loadingMessages && messages.length === 0) {
      initialMessageSent.current = true;
      window.history.replaceState({}, '', location.pathname);
      if (state.generateDocType) {
        setTimeout(() => {
          startGenerateRef.current?.({
            docType: state.generateDocType,
            description: state.generateDescription,
            playbookId: state.generatePlaybookId,
          });
        }, 50);
      } else {
        setIsGeneratePickerOpen(true);
      }
    }
  }, [chatId, loadingMessages, messages.length, location.state]);

  const handleMentionTextChange = useCallback((text: string) => {
    setMessageText(text);
    messageTextRef.current = text;
    // Close mention menu if @ has been deleted from the text
    if (isMentionMenuOpen && !text.includes('@')) {
      setIsMentionMenuOpen(false);
    }
  }, [isMentionMenuOpen]);

  const handleMentionsChange = useCallback((newMentions: Mention[]) => {
    setMentions(newMentions);
    mentionsRef.current = newMentions;
  }, []);

  const handleAtTrigger = useCallback(() => {
    if (projectId) {
      setMentionMenuPosition({ top: 10, left: 10 });
      setIsMentionMenuOpen(true);
    }
  }, [projectId]);

  const handleAtRemoved = useCallback(() => {
    setIsMentionMenuOpen(false);
  }, []);

  const handleMentionSelect = (type: 'file' | 'playbook', id: string) => {
    let name = '';
    if (type === 'playbook') {
      const pb = projectPlaybooks.find(p => p.playbookId === id);
      name = pb?.playbookName || t.qagentChat.contract.playbookFallback;
    } else {
      const file = projectFiles.find(f => f.id === id);
      name = file?.name || t.qagentChat.contract.fileFallback;
    }

    const mention: Mention = { type, id, name };

    // If a slash command action is pending, ignore - the review picker handles it
    if (pendingSkillAction) return;

    // Insert chip into the contenteditable, replacing the trailing @
    mentionInputRef.current?.insertMention(mention);

    // Add to mentions (avoid duplicates)
    setMentions(prev => {
      if (prev.some(m => m.id === id)) return prev;
      const updated = [...prev, mention];
      mentionsRef.current = updated;
      return updated;
    });

    setIsMentionMenuOpen(false);
  };

  // Clear conversation - reusable by both the Clear button and /clear command
  const handleClearConversation = useCallback(async () => {
    setMentions([]);
    mentionsRef.current = [];
    mentionInputRef.current?.clear();
    setMessageText('');
    messageTextRef.current = '';
    setMessages([]);
    setSuggestions([]);
    setSuggestionsAnchorMessageId(null);
    setHasStartedChat(false);
    setStreamingContent('');
    streamingContentRef.current = '';
    setFileToView(null);
    generationAbortRef.current?.abort();
    generationAbortRef.current = null;
    setActiveContractId(null);
    setIsGenerating(false);
    setIsStreaming(false);
    setActiveToolStatus(null);
    setGenerationProgress(null);
    if (chatId) {
      try {
        await clearQAgentChatContext(chatId);
      } catch (err) {
        console.error('[QAgentChat] Failed to clear backend context:', err);
      }
    }
  }, [chatId]);

  // Slash command handlers
  const handleSlashTrigger = useCallback((filter: string) => {
    setSlashFilter(filter);
    setIsSlashMenuOpen(true);
  }, []);

  const handleSlashRemoved = useCallback(() => {
    setIsSlashMenuOpen(false);
    setSlashFilter('');
  }, []);

  const handleSlashCommandSelect = useCallback((command: SlashCommand) => {
    setIsSlashMenuOpen(false);
    setSlashFilter('');

    const workflow = command.workflowId ? workflows.find(w => w.id === command.workflowId) ?? null : null;
    activeWorkflowRef.current = workflow;
    if (workflow && workflow.kind === 'prompt') {
      // A plain workflow pre-fills the message box to be edited and sent
      activeWorkflowRef.current = null;
      mentionInputRef.current?.setText(fillWorkflow(workflow.instructions));
      return;
    }

    if (command.type === 'action' && command.id === 'clear') {
      handleClearConversation();
      mentionInputRef.current?.clear();
      setMessageText('');
      messageTextRef.current = '';
      return;
    }

    if (command.type === 'action' && command.id === 'export') {
      mentionInputRef.current?.clear();
      setMessageText('');
      messageTextRef.current = '';

      // Contract export if a contract was generated
      if (activeContractId && chatId) {
        toast.info(t.qagentChat.toasts.exportingContract);
        (async () => {
          try {
            // Saves the contract to the project (once) and returns its download link
            const response = await exportContractAsProjectFile(chatId);
            if (response.success && response.data?.downloadUrl) {
              startDownload(response.data.downloadUrl, response.data.fileName);
              toast.success(t.qagentChat.toasts.exported(response.data.fileName));
            } else {
              toast.error(response.errors?.[0]?.detail || t.qagentChat.toasts.exportContractFailed);
            }
          } catch {
            toast.error(t.qagentChat.toasts.exportContractFailed);
          }
        })();
        return;
      }

      // File export with accepted suggestions
      const fileId = activeFileIds[0] || projectFiles.find(f => f.status === 'ready')?.id;
      if (!fileId || !chatId) {
        toast.error(t.qagentChat.toasts.noFileToExport);
        return;
      }
      toast.info(t.qagentChat.toasts.exportingDocument);
      (async () => {
        try {
          // A Word file goes out as a redline (tracked changes, no comments)
          const fileName = projectFiles.find(f => f.id === fileId)?.name ?? '';
          const response = await exportFileWithChanges(chatId, fileId, defaultExportOptions(fileName));
          if (response.success && response.data?.downloadUrl) {
            startDownload(response.data.downloadUrl, response.data.fileName);
            const skipped = response.data.skipped?.length ?? 0;
            if (skipped === 0) {
              toast.success(t.qagentChat.toasts.exported(response.data.fileName));
            } else if (!response.data.applied) {
              toast.warning(t.qagentChat.toasts.exportNothingApplied(skipped));
            } else {
              toast.warning(t.qagentChat.toasts.exportedWithSkipped(response.data.applied, skipped));
            }
          } else {
            toast.error(response.errors?.[0]?.detail || t.qagentChat.toasts.exportFileFailed);
          }
        } catch {
          toast.error(t.qagentChat.toasts.exportFileFailed);
        }
      })();
      return;
    }

    if (command.type === 'action' && command.id === 'generate') {
      mentionInputRef.current?.clear();
      setMessageText('');
      messageTextRef.current = '';
      setGenerateDocType('');
      setGenerateDescription('');
      setIsGeneratePickerOpen(true);
      return;
    }

    if (command.type === 'skill' && command.skillId) {
      // Check requirements
      if (command.requiresProject && !projectId) {
        toast.error(t.qagentChat.toasts.requiresProject);
        return;
      }
      const readyFiles = projectFiles.filter(f => f.status === 'ready');
      if (command.requiresFiles && readyFiles.length === 0) {
        toast.error(t.qagentChat.toasts.requiresFiles);
        return;
      }

      // Clear the slash command text
      mentionInputRef.current?.clear();
      setMessageText('');
      messageTextRef.current = '';

      // /search doesn't need a file picker - just set the skill for the next message
      if (command.skillId === 'search') {
        pendingSkillRef.current = command.skillId;
        return;
      }

      // Set pending action - this will show the file picker above the input
      setPendingSkillAction(command.skillId);
      setReviewSelectedFileId(null);
      setCompareSelectedFileIds([]);
      setCompareStandardId(workflow?.kind === 'compare' ? workflow.standardAgreementId : null);
      setReviewSelectedPlaybookId(workflow?.kind === 'review' ? workflow.playbookId : null);
      setReviewPickerIndex(0);
      reviewPickerIndexRef.current = 0;

      // Make the workflow's playbook one click away in this project
      const playbookId = workflow?.kind === 'review' ? workflow.playbookId : null;
      if (playbookId && projectId && !projectPlaybooks.some(p => p.playbookId === playbookId)) {
        void linkPlaybook(projectId, { playbookId }).then(async () => {
          const res = await getProjectPlaybooks(projectId);
          if (res.success && res.data) setProjectPlaybooks(res.data);
        }).catch(() => undefined);
      }
    }
  }, [handleClearConversation, projectId, projectFiles, projectPlaybooks, workflows]);
  applySkillRef.current = handleSlashCommandSelect;

  const handleStartSkill = useCallback(async () => {
    if (!pendingSkillAction) return;

    const skillId = pendingSkillAction;
    const isCompare = skillId === 'compare';

    // Validate selection
    if (isCompare) {
      const chosen = compareSelectedFileIds.length + (compareStandardId ? 1 : 0);
      if (chosen !== 2) return;
    } else {
      if (!reviewSelectedFileId) return;
    }

    // Determine files and build prompt based on skill type
    let prompt: string;
    let fileIds: string[];

    if (isCompare && compareStandardId) {
      // A standard agreement runs the comparison engine; the chat shows a card
      // with its progress and result. A workflow's instructions become the focus.
      const standard = standardAgreements.find(a => a.id === compareStandardId);
      const counterparty = projectFiles.find(f => f.id === compareSelectedFileIds[0]);
      if (!projectId || !standard || !counterparty || !chatId) return;
      const workflow = activeWorkflowRef.current;
      const focus = workflow?.instructions.trim()
        ? fillWorkflow(workflow.instructions, counterparty.name).slice(0, 8000)
        : undefined;
      const targetChatId = chatId;
      setIsPreparingCompare(true);
      try {
        const started = await startComparison({
          standardAgreementId: standard.id,
          receivedFileId: counterparty.id,
          focus,
          language: locale === 'sv' ? 'sv' : 'en',
        });
        if (!started.success || !started.data) {
          toast.error(started.errors?.[0]?.detail || started.errors?.[0]?.title || t.comparisons.startFailed);
          return;
        }
        const card = await addComparisonCard(targetChatId, started.data.id);
        if (!card.success || !card.data) {
          toast.error(t.comparisons.startFailed);
          return;
        }
        if (chatIdRef.current === targetChatId) {
          activeWorkflowRef.current = null;
          setMessages(prev => [...prev, card.data!]);
          setHasStartedChat(true);
          setPendingSkillAction(null);
          setReviewSelectedFileId(null);
          setCompareSelectedFileIds([]);
          setCompareStandardId(null);
          setReviewSelectedPlaybookId(null);
          setReviewPickerIndex(0);
        }
      } finally {
        setIsPreparingCompare(false);
      }
      return;
    } else if (isCompare) {
      setPendingSkillAction(null);
      fileIds = compareSelectedFileIds;
      const file1 = projectFiles.find(f => f.id === compareSelectedFileIds[0]);
      const file2 = projectFiles.find(f => f.id === compareSelectedFileIds[1]);
      prompt = t.qagentChat.contract.compare(
        file1?.name || t.qagentChat.contract.party1,
        file2?.name || t.qagentChat.contract.party2,
      );
      // Open first file in viewer
      if (file1) setFileToView(file1);
    } else {
      setPendingSkillAction(null);
      fileIds = [reviewSelectedFileId!];
      const file = projectFiles.find(f => f.id === reviewSelectedFileId);
      if (file) setFileToView(file);

      const fileName = file?.name || t.qagentChat.contract.documentFallback;

      switch (skillId) {
        case 'review': {
          const pbName = reviewSelectedPlaybookId
            ? projectPlaybooks.find(p => p.playbookId === reviewSelectedPlaybookId)?.playbookName
            : null;
          prompt = pbName
            ? t.qagentChat.contract.reviewWithPlaybook(fileName, pbName)
            : t.qagentChat.contract.reviewThorough(fileName);
          break;
        }
        case 'summarize':
          prompt = t.qagentChat.contract.summarize(fileName);
          break;
        case 'redline-summary':
          prompt = t.qagentChat.contract.redlineSummary(fileName);
          break;
        default:
          prompt = t.qagentChat.contract.analyze(fileName);
      }
    }

    // A workflow adds its own instructions to the request
    const workflow = activeWorkflowRef.current;
    activeWorkflowRef.current = null;
    if (workflow?.instructions.trim()) {
      const documentName = projectFiles.find(f => f.id === fileIds[0])?.name;
      prompt = `${prompt}\n\n${fillWorkflow(workflow.instructions, documentName)}`;
    }

    // Set active context
    setActiveFileIds(fileIds);

    // Only use playbook for review skill
    if (skillId === 'review' && reviewSelectedPlaybookId) {
      setActivePlaybookId(reviewSelectedPlaybookId);
    } else {
      setActivePlaybookId(null);
    }

    // Clear mentions - file is passed via activeFileIds, not mention tokens
    mentionsRef.current = [];
    setMentions([]);

    // Auto-send
    setTimeout(() => {
      const origSend = sendMessageRef.current;
      if (origSend) {
        pendingSkillRef.current = skillId;
        origSend(prompt);
      }
    }, 100);

    // Reset picker state
    setReviewSelectedFileId(null);
    setCompareSelectedFileIds([]);
    setCompareStandardId(null);
    setReviewSelectedPlaybookId(null);
    setReviewPickerIndex(0);
  }, [pendingSkillAction, reviewSelectedFileId, compareSelectedFileIds, compareStandardId, standardAgreements, projectId, chatId, locale, t, reviewSelectedPlaybookId, projectFiles, projectPlaybooks]);

  useEffect(() => {
    let cancelled = false;
    listWorkflows().then(res => {
      if (!cancelled && res.success && res.data) setWorkflows(res.data);
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  const slashCommands = useMemo<SlashCommand[]>(() => {
    const taken = new Set(SLASH_COMMANDS.map(c => c.id));
    const extra = workflows.map(w => {
      const base = w.name.toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'workflow';
      let id = base;
      for (let n = 2; taken.has(id); n++) id = `${base}-${n}`;
      taken.add(id);
      const skill = w.kind !== 'prompt';
      return {
        id,
        name: w.name,
        description: w.description || w.name,
        icon: WorkflowIcon,
        type: skill ? 'skill' : 'action',
        skillId: skill ? w.kind : undefined,
        requiresProject: skill,
        requiresFiles: skill,
        workflowId: w.id,
      } as SlashCommand;
    });
    return [...SLASH_COMMANDS, ...extra];
  }, [workflows]);

  // The compare picker also offers the organization's standard agreements
  useEffect(() => {
    if (pendingSkillAction !== 'compare') return;
    let cancelled = false;
    listStandardAgreements().then(res => {
      if (!cancelled && res.success && res.data) setStandardAgreements(res.data);
    });
    return () => { cancelled = true; };
  }, [pendingSkillAction]);

  const handleStartGenerate = useCallback(async (
    overrides?: { docType?: string; description?: string; playbookId?: string | null },
  ) => {
    const docType = (overrides?.docType ?? generateDocType).trim();
    const description = (overrides?.description ?? generateDescription).trim();
    const playbookId = overrides?.playbookId !== undefined ? overrides.playbookId : activePlaybookId;
    if (!docType) {
      toast.error(t.qagentChat.toasts.enterDocType);
      return;
    }
    if (!chatId) return;
    const targetChatId = chatId;
    const isActive = () => chatIdRef.current === targetChatId;

    setIsGeneratePickerOpen(false);
    setHasStartedChat(true);
    setActiveSkillId(null);

    // Show user message
    const userMessage: QAgentMessage = {
      id: `temp-${Date.now()}`,
      content: buildGeneratePromptText(docType, description),
      type: 'human',
      createdAt: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessage]);
    setTimeout(() => scrollToBottom(), 50);

    // Step 1: INIT stage - get form fields
    setIsGenerating(true);
    setIsStreaming(true);
    setStreamingContent('');
    setActiveToolStatus('thinking');
    try {
      const initState: ContractState = {
        stage: ContractStage.INIT,
        documentType: docType,
        documentDescription: description || docType,
        playbookId: playbookId || undefined,
      };
      const initResponse = await submitContractState(initState, targetChatId);

      if (!initResponse.success || !initResponse.data) {
        if (!isActive()) return;
        // Surface the specific reason (e.g. trial cap hit) instead of a
        // generic "Failed to initialize" message.
        const errorMsg =
          initResponse.errors?.[0]?.detail ||
          initResponse.errors?.[0]?.title ||
          t.qagentChat.toasts.initContractFailed;
        toast.error(errorMsg);
        setIsGenerating(false);
        setIsStreaming(false);
        setActiveToolStatus(null);
        return;
      }

      const initData = initResponse.data;
      const contractId = initData.contractId || null;

      // Persist the contract-chat link, even if the user already left: it
      // drives reload hydration and lets the backend finish generation and
      // save the file if the user leaves later on
      if (contractId) {
        const linkResponse = await linkContractToChat(targetChatId, contractId);
        if (!linkResponse.success) {
          console.error('[QAgentChat] Failed to link contract to chat:', linkResponse.errors);
        } else if (linkResponse.data?.title) {
          // The backend names a contract chat after its document
          updateQAgentChatTitle(targetChatId, linkResponse.data.title);
          if (isActive()) setChatTitle(linkResponse.data.title);
        }
      }
      if (!isActive()) return;
      setActiveContractId(contractId);

      // Show form fields as an AI message with interactive form
      setMessages(prev => [...prev, buildContractFormMessage(initData, String(Date.now()))]);
      setActiveToolStatus(null);
      setIsGenerating(false);
      setIsStreaming(false);
      setTimeout(() => scrollToBottom(), 100);
    } catch (err) {
      if (!isActive()) return;
      toast.error(t.qagentChat.toasts.startContractFailed);
      setIsGenerating(false);
      setIsStreaming(false);
      setActiveToolStatus(null);
    }
  }, [chatId, generateDocType, generateDescription, activePlaybookId, scrollToBottom, updateQAgentChatTitle]);

  /**
   * Save the generated contract to project files. The backend usually has
   * already done this when generation finished (it's idempotent); this picks
   * up the file, and creates the project if the chat had none.
   */
  const saveContractToProject = useCallback(async (
    messageId: string,
    options: { openViewer?: boolean } = {},
  ) => {
    if (!chatId) return;
    const targetChatId = chatId;
    const isActive = () => chatIdRef.current === targetChatId;

    patchMessage(messageId, { _saveStatus: 'saving' });
    try {
      const exportResponse = await exportContractAsProjectFile(targetChatId);
      if (!isActive()) return;
      if (!exportResponse.success || !exportResponse.data) {
        patchMessage(messageId, { _saveStatus: 'failed' });
        toast.error(
          exportResponse.errors?.[0]?.detail ||
          exportResponse.errors?.[0]?.title ||
          t.qagentChat.toasts.saveContractFailed
        );
        return;
      }

      const {
        fileId,
        fileName,
        projectId: savedProjectId,
        projectName: savedProjectName,
        projectCreated,
      } = exportResponse.data;

      // Use the real file record rather than a locally built one
      const filesResponse = await getProjectFiles(savedProjectId);
      if (!isActive()) return;
      const file: ProjectFile = filesResponse.data?.find(f => f.id === fileId) ?? {
        id: fileId,
        name: fileName,
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        size: 0,
        status: 'ready',
        projectId: savedProjectId,
        uploadedById: '',
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      if (savedProjectId !== projectIdRef.current) {
        // The chat is now attached to this (possibly new) project; the
        // projectId effect loads its files
        setProjectId(savedProjectId);
        setProjectName(savedProjectName);
        updateQAgentChatProject(targetChatId, savedProjectId, savedProjectName);
      } else if (filesResponse.success && filesResponse.data) {
        setProjectFiles(filesResponse.data);
        setActiveFileIds(prev => (prev.includes(fileId) ? prev : [...prev, fileId]));
      }
      // Refresh project file counts elsewhere in the app
      fetchProjects();
      // Saving names the chat after the document and its parties
      void getQAgentChat(targetChatId).then((chatResponse) => {
        const title = chatResponse.success ? chatResponse.data?.title : null;
        if (!title) return;
        updateQAgentChatTitle(targetChatId, title);
        if (isActive()) setChatTitle(title);
      });

      patchMessage(messageId, {
        _saveStatus: 'saved',
        _contractFile: file,
        _projectName: savedProjectName,
      });
      if (options.openViewer) {
        setFileToView(file);
      }
      if (savedProjectName) {
        toast.success(
          projectCreated
            ? t.qagentChat.toasts.contractSavedNewProject(fileName, savedProjectName)
            : t.qagentChat.toasts.contractSaved(fileName, savedProjectName)
        );
      }
    } catch (err) {
      console.error('[QAgentChat] Failed to save contract to project:', err);
      if (!isActive()) return;
      patchMessage(messageId, { _saveStatus: 'failed' });
      toast.error(t.qagentChat.toasts.saveContractFailed);
    }
  }, [chatId, fetchProjects, updateQAgentChatProject, updateQAgentChatTitle]);

  /**
   * Wait for a contract's sections to be generated (after the details were
   * submitted, or when reopening a chat mid-generation), then show the
   * result and save it to project files.
   */
  const runContractGeneration = useCallback(async (contractId: string) => {
    if (!chatId) return;
    const targetChatId = chatId;

    generationAbortRef.current?.abort();
    const controller = new AbortController();
    generationAbortRef.current = controller;
    const { signal } = controller;

    setActiveContractId(contractId);
    setIsGenerating(true);
    setIsStreaming(true);
    setStreamingContent('');
    // Status keys, not translated text: the chat message translates them and picks the orb
    setActiveToolStatus('Processing details');

    const documentType = (await getContract(contractId)).data?.documentType ?? '';
    let result;
    try {
      result = await waitForContractGeneration(contractId, targetChatId, {
        signal,
        onProgress: (progress, stage, liveSections) => {
          if (signal.aborted) return;
          setActiveToolStatus(stage || 'generating');
          setGenerationProgress(progress);
          if (liveSections?.some(s => s.content || s.subSections?.length)) {
            setLiveContract({ documentType, sections: liveSections });
          }
        },
      });
    } catch (err) {
      result = { success: false as const, status: 500, errors: undefined };
    }
    // Left the chat: the backend finishes and saves it, and it's resumed on return
    if (signal.aborted) return;
    if (generationAbortRef.current === controller) {
      generationAbortRef.current = null;
    }

    setIsGenerating(false);
    setIsStreaming(false);
    setActiveToolStatus(null);
    setGenerationProgress(null);
    setLiveContract(null);

    if (!result.success || !result.data?.sections?.length) {
      // Cancelled: nothing to retry. Otherwise offer to run it again rather
      // than showing the backend's technical error
      if (result.status === 499) {
        toast.error(result.errors?.[0]?.detail || t.qagentChat.toasts.contractGenerationFailed);
      } else {
        setMessages(prev => [...prev, buildContractFailedMessage(contractId, String(Date.now()))]);
        setTimeout(() => scrollToBottom(), 100);
      }
      return;
    }

    const sectionsMessage = buildContractSectionsMessage(result.data, String(Date.now()), {
      saveStatus: 'saving',
    });
    setMessages(prev => [...prev, sectionsMessage]);
    setTimeout(() => scrollToBottom(), 200);

    await saveContractToProject(sectionsMessage.id, { openViewer: true });
  }, [chatId, scrollToBottom, saveContractToProject]);

  resumeGenerationRef.current = runContractGeneration;

  /**
   * Run a failed generation again from the details already entered: a fresh
   * SECTIONS job, or the details form again if those were never processed.
   */
  const handleRetryGeneration = useCallback(async (failedMessageId: string, contractId: string) => {
    if (!chatId) return;
    const targetChatId = chatId;
    const isActive = () => chatIdRef.current === targetChatId;

    setMessages(prev => prev.filter(m => m.id !== failedMessageId));
    const restoreFailed = () =>
      setMessages(prev => [...prev, buildContractFailedMessage(contractId, String(Date.now()))]);

    const contractResponse = await getContract(contractId);
    if (!isActive()) return;
    if (!contractResponse.success || !contractResponse.data) {
      restoreFailed();
      toast.error(t.qagentChat.toasts.contractGenerationFailed);
      return;
    }

    // The draft from the failed run isn't part of the contract's state
    const { liveSections: _draft, ...state } = contractResponse.data as ContractState & { liveSections?: unknown };
    setActiveContractId(contractId);

    if (state.stage <= ContractStage.INFO) {
      setMessages(prev => [...prev, buildContractFormMessage(state, String(Date.now()))]);
      setTimeout(() => scrollToBottom(), 100);
      return;
    }

    const submit = await submitContractState({ ...state, stage: ContractStage.SECTIONS }, targetChatId);
    if (!isActive()) return;
    // 409: a job for this contract is already running; just follow it
    if (!submit.success && submit.status !== 409) {
      restoreFailed();
      toast.error(submit.errors?.[0]?.detail || t.qagentChat.toasts.contractGenerationFailed);
      return;
    }
    await runContractGeneration(contractId);
  }, [chatId, runContractGeneration, scrollToBottom]);

  const handleContractFormSubmit = useCallback(async (
    parties: ContractPartyField[],
    otherInfo: ContractPartyField[],
  ) => {
    if (!activeContractId || !chatId) return;
    const targetChatId = chatId;
    const isActive = () => chatIdRef.current === targetChatId;

    // Get the stored init state from this contract's form message; a chat can
    // hold several contracts, and the first form belongs to an earlier one
    const formMessages = messages.filter(m => m.id.startsWith('contract-form-'));
    const formMsg =
      formMessages.find(m => (m as any)._contractForm?.contractId === activeContractId) ??
      formMessages[formMessages.length - 1];
    const initState = (formMsg as any)?._contractForm?.state as ContractState | undefined;
    // The form collapses into a summary of the values used
    if (formMsg) {
      patchMessage(formMsg.id, {
        _contractForm: { ...(formMsg as any)._contractForm, parties, otherInfo, submitted: true },
      });
    }
    setTimeout(() => scrollToBottom(), 50);
    if (!initState) {
      toast.error(t.qagentChat.toasts.contractStateNotFound);
      return;
    }

    // Step 2: INFO stage with filled parties. The backend then runs SECTIONS
    // and saves the result to project files on its own.
    // Clean up empty userResponse fields - validator requires undefined or non-empty string
    const cleanFields = (fields: ContractPartyField[]) =>
      fields.map(f => ({
        ...f,
        userResponse: f.userResponse?.trim() || undefined,
      }));

    setIsGenerating(true);
    setIsStreaming(true);
    setStreamingContent('');
    setActiveToolStatus('thinking');
    try {
      const infoState: ContractState = {
        ...initState,
        stage: ContractStage.INFO,
        parties: cleanFields(parties),
        otherUserInformation: cleanFields(otherInfo),
      };
      const infoResponse = await submitContractState(infoState, targetChatId);
      if (!isActive()) return;

      const contractId = infoResponse.data?.contractId;
      if (!infoResponse.success || !contractId) {
        toast.error(
          infoResponse.success ? t.qagentChat.toasts.noContractId : t.qagentChat.toasts.processDetailsFailed
        );
        setIsGenerating(false);
        setIsStreaming(false);
        setActiveToolStatus(null);
        return;
      }

      await runContractGeneration(contractId);
    } catch (err) {
      if (!isActive()) return;
      toast.error(t.qagentChat.toasts.contractGenerationFailed);
      setIsGenerating(false);
      setIsStreaming(false);
      setActiveToolStatus(null);
    }
  }, [activeContractId, chatId, messages, scrollToBottom, runContractGeneration]);

  // Expose for the init effect (declared earlier) so it can fire generate
  // directly when arriving with prefilled values, without relying on state
  // settling first.
  startGenerateRef.current = handleStartGenerate;

  const handleSelectProject = async (newProjectId: string | null) => {
    if (!chatId) return;

    try {
      const response = await attachProjectToChat(chatId, newProjectId);
      if (response.success) {
        setProjectId(newProjectId);
        if (newProjectId) {
          const project = projects.find(p => p.id === newProjectId);
          setProjectName(project?.name || null);
        } else {
          setProjectName(null);
          // Close context panel and clear mentions when project is detached
          setMentions([]);
          mentionsRef.current = [];
          mentionInputRef.current?.clear();
          setMessageText('');
          messageTextRef.current = '';
          if (isContextPanelOpen) {
            setIsContextPanelOpen(false);
            if (sidebarWasOpenRef.current) {
              setSidebarOpen(true);
            }
          }
        }
        toast.success(newProjectId ? t.qagentChat.toasts.projectAttached : t.qagentChat.toasts.projectDetached);
      }
    } catch (error) {
      toast.error(t.qagentChat.toasts.updateProjectFailed);
    }
  };

  const handleSelectPlaybook = (playbookId: string | null) => {
    setActivePlaybookId(playbookId);
  };

  // Context panel toggle - also manages main sidebar visibility
  const handleToggleContextPanel = () => {
    if (!isContextPanelOpen) {
      // Opening context panel - remember sidebar state and close it
      sidebarWasOpenRef.current = isSidebarOpen;
      if (isSidebarOpen) {
        setSidebarOpen(false);
      }
      setIsContextPanelOpen(true);
    } else {
      // Closing context panel - restore sidebar if it was open
      setIsContextPanelOpen(false);
      if (sidebarWasOpenRef.current) {
        setSidebarOpen(true);
      }
    }
  };

  // File upload handlers
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Uploads a pick or drop (one file or several) and puts them in the chat's context
  const { screen: screenUploads, upload: uploadFiles } = uploads;
  const uploadAndAttachFiles = useCallback(async (files: File[]): Promise<void> => {
    if (!projectId || files.length === 0) return;

    const { uploaded } = await uploadFiles(projectId, files, {
      onRetry: (failed) => void uploadAndAttachFiles(failed),
    });
    if (uploaded.length === 0) return;

    // Once for the whole batch, not once per file. Every playbook stays
    // referenced so it is one click away; best-effort, it must not fail the upload.
    const [filesResponse, linkAllResponse] = await Promise.all([
      getProjectFiles(projectId),
      linkAllPlaybooks(projectId).catch((err) => {
        console.error('[QAgentChat] Failed to auto-link playbooks:', err);
        return null;
      }),
    ]);
    fetchProjects();
    // The chat may have moved to another project meanwhile (a late Retry):
    // the files are in, but this view no longer shows that project
    if (projectIdRef.current !== projectId) return;
    if (filesResponse.success && filesResponse.data) {
      setProjectFiles(filesResponse.data);
      setActiveFileIds(prev => [...prev, ...uploaded.map(f => f.id)]);
    }
    if (linkAllResponse?.success && linkAllResponse.data) {
      setProjectPlaybooks(linkAllResponse.data);
    }
  }, [projectId, uploadFiles, fetchProjects]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    // Copy before clearing the input: clearing empties the live FileList
    const files = Array.from(event.target.files ?? []);
    if (fileInputRef.current) fileInputRef.current.value = '';
    await uploadAndAttachFiles(screenUploads(files));
  };

  const handleFilesDropped = useCallback(async (files: FileList) => {
    await uploadAndAttachFiles(screenUploads(files));
  }, [uploadAndAttachFiles, screenUploads]);

  // Playbook linking handlers
  const handleOpenLinkDialog = () => {
    setIsLinkDialogOpen(true);
  };

  const handleLinkPlaybook = async () => {
    if (!selectedPlaybookToLink || !projectId) return;

    setIsLinking(true);
    try {
      const response = await linkPlaybook(projectId, { playbookId: selectedPlaybookToLink });
      if (response.success) {
        toast.success(t.qagentChat.toasts.playbookLinked);
        setIsLinkDialogOpen(false);
        setSelectedPlaybookToLink('');
        // Refresh project playbooks
        const playbooksResponse = await getProjectPlaybooks(projectId);
        if (playbooksResponse.success && playbooksResponse.data) {
          setProjectPlaybooks(playbooksResponse.data);
        }
      } else {
        const errorMsg = response.errors?.[0]?.title || t.qagentChat.toasts.linkPlaybookFailed;
        toast.error(errorMsg);
      }
    } catch (error) {
      toast.error(t.qagentChat.toasts.linkPlaybookError);
      console.error('Link error:', error);
    } finally {
      setIsLinking(false);
    }
  };

  // Get available playbooks (not already linked to this project)
  const linkedPlaybookIds = new Set(projectPlaybooks.map((p) => p.playbookId));
  const availablePlaybooks = playbooks.filter((p) => !linkedPlaybookIds.has(p.id));

  // Delete file handler
  const handleDeleteFile = async () => {
    if (!fileToDelete || !projectId) return;

    setIsDeletingFile(true);
    try {
      const response = await deleteProjectFile(projectId, fileToDelete.id);
      if (response.success) {
        toast.success(t.qagentChat.toasts.fileDeleted(fileToDelete.name));
        // Remove from local state
        setProjectFiles(prev => prev.filter(f => f.id !== fileToDelete.id));
        setActiveFileIds(prev => prev.filter(id => id !== fileToDelete.id));
        // Remove suggestions for the deleted file (DB cascades, but sync frontend state)
        setSuggestions(prev => prev.filter(s => s.fileId !== fileToDelete.id));
        // Close file viewer if the deleted file is currently open
        if (fileToView?.id === fileToDelete.id) {
          setFileToView(null);
          setScrollToSuggestionId(null);
        }
        // Refresh projects to update file count in selector
        fetchProjects();
      } else {
        toast.error(t.qagentChat.toasts.deleteFileFailed);
      }
    } catch (error) {
      toast.error(t.qagentChat.toasts.deleteFileError);
      console.error('Delete error:', error);
    } finally {
      setIsDeletingFile(false);
      setFileToDelete(null);
    }
  };

  // Unlink playbook handler
  const handleUnlinkPlaybook = async () => {
    if (!playbookToUnlink || !projectId) return;

    setIsUnlinkingPlaybook(true);
    try {
      const response = await unlinkPlaybook(projectId, playbookToUnlink.playbookId);
      if (response.success) {
        toast.success(t.qagentChat.toasts.playbookUnlinked(playbookToUnlink.playbookName));
        // Remove from local state
        setProjectPlaybooks(prev => prev.filter(p => p.id !== playbookToUnlink.id));
        // Clear active playbook if it was the one unlinked
        if (activePlaybookId === playbookToUnlink.playbookId) {
          setActivePlaybookId(null);
        }
      } else {
        toast.error(t.qagentChat.toasts.unlinkPlaybookFailed);
      }
    } catch (error) {
      toast.error(t.qagentChat.toasts.unlinkPlaybookError);
      console.error('Unlink error:', error);
    } finally {
      setIsUnlinkingPlaybook(false);
      setPlaybookToUnlink(null);
    }
  };

  // Suggestion handlers
  const handleAcceptSuggestion = async (suggestionId: string) => {
    try {
      const response = await updateSuggestionStatus(suggestionId, 'accepted');
      if (response.success) {
        setSuggestions(prev =>
          prev.map(s => s.id === suggestionId ? { ...s, status: 'accepted' } : s)
        );
        toast.success(t.qagentChat.toasts.suggestionAccepted);
      } else {
        toast.error(t.qagentChat.toasts.acceptSuggestionFailed);
      }
    } catch (error) {
      toast.error(t.qagentChat.toasts.acceptSuggestionError);
      console.error('Accept error:', error);
    }
  };

  // The document preview applies accepted edits by re-matching their text in
  // the rendered docx. When a change can't be located (text drifted, fuzzy
  // mismatch), warn the user instead of leaving them thinking it applied.
  const handleSuggestionsApplyFailed = useCallback((ids: string[]) => {
    if (ids.length > 0) {
      toast.warning(t.qagentChat.toasts.suggestionApplyFailed(ids.length));
    }
  }, [t]);

  const getRuleQuotes = useCallback((messageId: string, ruleId: string): string[] => {
    const message = messages.find(m => m.id === messageId) as any;
    const quotes: string[] = [];
    const collect = (items?: Array<ContractSection | ContractSubSection>) =>
      items?.forEach(item => {
        item.annotations?.forEach(annotation => {
          if (annotation.ruleId === ruleId && annotation.text) quotes.push(annotation.text);
        });
        collect(item.subSections);
      });
    collect(message?._contractSections?.sections);
    return quotes;
  }, [messages]);

  const handleRuleSelect = (message: QAgentMessage, ruleId: string) => {
    if (selectedRule?.messageId === message.id && selectedRule.ruleId === ruleId) {
      setSelectedRule(null);
      return;
    }
    setSelectedRule({ messageId: message.id, ruleId });
    const file = (message as any)._contractFile as ProjectFile | undefined;
    if (file && fileToView?.id !== file.id) setFileToView(file);
    setScrollToRuleQuote(getRuleQuotes(message.id, ruleId)[0] ?? null);
  };

  const activeRule = hoveredRule ?? selectedRule;
  const ruleHighlightQuotes = useMemo(() => {
    if (!activeRule || !fileToView) return [];
    const message = messages.find(m => m.id === activeRule.messageId) as any;
    if (message?._contractFile?.id !== fileToView.id) return [];
    return getRuleQuotes(activeRule.messageId, activeRule.ruleId);
  }, [activeRule, fileToView, messages, getRuleQuotes]);

  const handleRejectSuggestion = async (suggestionId: string) => {
    try {
      const response = await updateSuggestionStatus(suggestionId, 'rejected');
      if (response.success) {
        setSuggestions(prev =>
          prev.map(s => s.id === suggestionId ? { ...s, status: 'rejected' } : s)
        );
        toast.success(t.qagentChat.toasts.suggestionRejected);
      } else {
        toast.error(t.qagentChat.toasts.rejectSuggestionFailed);
      }
    } catch (error) {
      toast.error(t.qagentChat.toasts.rejectSuggestionError);
      console.error('Reject error:', error);
    }
  };

  const handleUndoSuggestion = async (suggestionId: string) => {
    try {
      const response = await undoSuggestionApi(suggestionId);
      if (response.success) {
        setSuggestions(prev =>
          prev.map(s => s.id === suggestionId ? { ...s, status: 'pending' } : s)
        );
        toast.success(t.qagentChat.toasts.suggestionUndone);
      } else {
        toast.error(t.qagentChat.toasts.undoSuggestionFailed);
      }
    } catch (error) {
      toast.error(t.qagentChat.toasts.undoSuggestionError);
      console.error('Undo error:', error);
    }
  };

  const handleAcceptAllSuggestions = async () => {
    if (!chatId) return;
    try {
      const response = await acceptAllSuggestions(chatId);
      if (response.success) {
        setSuggestions(prev =>
          prev.map(s => s.status === 'pending' ? { ...s, status: 'accepted' } : s)
        );
        toast.success(t.qagentChat.toasts.suggestionsAccepted(response.data?.affected || 0));
      } else {
        toast.error(t.qagentChat.toasts.acceptSuggestionsFailed);
      }
    } catch (error) {
      toast.error(t.qagentChat.toasts.acceptSuggestionsError);
      console.error('Accept all error:', error);
    }
  };

  const handleRejectAllSuggestions = async () => {
    if (!chatId) return;
    try {
      const response = await rejectAllSuggestions(chatId);
      if (response.success) {
        setSuggestions(prev =>
          prev.map(s => s.status === 'pending' ? { ...s, status: 'rejected' } : s)
        );
        toast.success(t.qagentChat.toasts.suggestionsRejected(response.data?.affected || 0));
      } else {
        toast.error(t.qagentChat.toasts.rejectSuggestionsFailed);
      }
    } catch (error) {
      toast.error(t.qagentChat.toasts.rejectSuggestionsError);
      console.error('Reject all error:', error);
    }
  };

  const handleViewSuggestionInDocument = (suggestion: QAgentSuggestion) => {
    // Find the file associated with the suggestion
    const file = projectFiles.find(f => f.id === suggestion.fileId);
    if (file) {
      setFileToView(file);
      setScrollToSuggestionId(suggestion.id);
    } else {
      toast.error(t.qagentChat.toasts.fileNotFound);
    }
  };

  // Download a document with its accepted suggestions, as a redline or clean.
  const handleExportFileWithEdits = async (
    fileId: string,
    _fileName: string,
    options: ExportFileOptions,
  ) => {
    if (!chatId || exportingFileId) return;
    setExportingFileId(fileId);
    toast.info(t.qagentChat.toasts.preparingDocument);
    try {
      const response = await exportFileWithChanges(chatId, fileId, options);
      if (response.success && response.data?.downloadUrl) {
        startDownload(response.data.downloadUrl, response.data.fileName);
        const skipped = response.data.skipped?.length ?? 0;
        if (skipped === 0) {
          toast.success(t.qagentChat.toasts.downloaded(response.data.fileName));
        } else if (!response.data.applied) {
          toast.warning(t.qagentChat.toasts.exportNothingApplied(skipped));
        } else {
          toast.warning(t.qagentChat.toasts.exportedWithSkipped(response.data.applied, skipped));
        }
        // A clean export marks suggestions as exported server-side; refresh so
        // they drop out of the panel (a redline leaves them as they are).
        await fetchSuggestions();
      } else {
        toast.error(response.errors?.[0]?.detail || t.qagentChat.toasts.downloadDocumentFailed);
      }
    } catch (error) {
      console.error('[QAgentChat] Export with edits failed:', error);
      toast.error(t.qagentChat.toasts.downloadDocumentFailed);
    } finally {
      setExportingFileId(null);
    }
  };

  // Regenerate: remove last AI message and stream a new response (no user bubble)
  const handleRegenerate = useCallback(async () => {
    if (isStreaming || !chatId) return;
    const lastHuman = [...messages].reverse().find(m => m.type === 'human');
    const lastAi = [...messages].reverse().find(m => m.type === 'ai' && !m.comparisonId);
    if (!lastHuman || !lastAi) return;

    // Remove the last AI message from the UI
    setMessages(prev => {
      let lastAiIdx = -1;
      for (let i = prev.length - 1; i >= 0; i--) {
        if (prev[i].type === 'ai' && !prev[i].comparisonId) { lastAiIdx = i; break; }
      }
      if (lastAiIdx === -1) return prev;
      return prev.filter((_, i) => i !== lastAiIdx);
    });

    // Stream directly - no user message bubble
    setIsStreaming(true);
    setStreamingContent('');
    streamingContentRef.current = '';

    const regenPrompt = `The user wants a different answer. Their original question was:\n\n${lastHuman.content}\n\nYour previous response was:\n\n${lastAi.content}\n\nPlease provide a different, improved response.`;

    abortControllerRef.current?.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      await streamQAgentMessage(
        chatId,
        { message: regenPrompt },
        (chunk) => {
          streamingContentRef.current += chunk;
          setStreamingContent(streamingContentRef.current);
        },
        undefined,
        (error) => {
          if (abortController.signal.aborted) return;
          toast.error(error);
          setIsStreaming(false);
          setStreamingContent('');
          streamingContentRef.current = '';
          setActiveToolStatus(null);
        },
        async () => {
          const finalContent = streamingContentRef.current;
          if (finalContent) {
            setMessages(prev => [...prev, {
              id: `ai-${Date.now()}`,
              content: finalContent,
              type: 'ai',
              createdAt: new Date().toISOString(),
            } as QAgentMessage]);
          }
          setIsStreaming(false);
          setStreamingContent('');
          streamingContentRef.current = '';
          setActiveToolStatus(null);
        },
        abortController.signal,
        (toolName, status) => {
          setActiveToolStatus(status === 'start' ? toolName : null);
        },
      );
    } catch {
      if (!abortController.signal.aborted) {
        toast.error(t.qagentChat.toasts.regenerateFailed);
        setIsStreaming(false);
        setStreamingContent('');
        streamingContentRef.current = '';
      }
    }
  }, [isStreaming, messages, chatId]);

  const sendMessageRef = useRef<(content: string) => Promise<void>>();

  const sendMessage = async (content: string) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!chatId || !uuidRegex.test(chatId) || !content.trim()) {
      return;
    }

    setHasStartedChat(true);
    setFailedMessage(null);

    // Capture mentions from the ref (always current, avoids stale closure)
    const currentMentions = mentionsRef.current;

    // Convert tokens to @[Name] for display in chat bubbles
    const displayContent = currentMentions.reduce((text, m) => {
      return text.replace(`[[${m.type}:${m.id}]]`, `@[${m.name}]`);
    }, content.trim());

    const userMessage: QAgentMessage = {
      id: `temp-${Date.now()}`,
      content: displayContent,
      type: 'human',
      createdAt: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessage]);
    // Remove first queued display bubble if this is a dequeued message
    setQueuedMessages(prev => prev.length > 0 ? prev.slice(1) : prev);
    setIsStreaming(true);
    setStreamingContent('');
    streamingContentRef.current = '';
    setMessageText('');
    messageTextRef.current = '';
    mentionInputRef.current?.clear();

    // Scroll to bottom after the user message renders
    setTimeout(() => scrollToBottom(), 50);

    // Build request with context
    const requestData: StreamQAgentMessageDto = {
      message: content.trim(),
    };

    // Include pending skill from slash command action
    if (pendingSkillRef.current) {
      requestData.skillId = pendingSkillRef.current;
      setActiveSkillId(pendingSkillRef.current);
      pendingSkillRef.current = null;
    } else {
      setActiveSkillId(null);
    }

    // Use mentions for playbook/file context, falling back to active selections
    const mentionedPlaybook = currentMentions.find(m => m.type === 'playbook');
    if (mentionedPlaybook) {
      requestData.playbookId = mentionedPlaybook.id;
    } else if (activePlaybookId) {
      requestData.playbookId = activePlaybookId;
    }

    const mentionedFileIds = currentMentions.filter(m => m.type === 'file').map(m => m.id);
    if (mentionedFileIds.length > 0) {
      // Merge mentioned files with already-active files
      requestData.fileIds = [...new Set([...activeFileIds, ...mentionedFileIds])];
    } else if (activeFileIds.length > 0) {
      requestData.fileIds = activeFileIds;
    }

    // Clear mentions after capturing them
    setMentions([]);
    mentionsRef.current = [];

    console.log('[QAgentChat] Sending message with context:', {
      message: requestData.message,
      playbookId: requestData.playbookId,
      fileIds: requestData.fileIds,
      mentionsCaptured: currentMentions,
    });

    // Abort any existing stream before starting a new one
    abortControllerRef.current?.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    pendingNoticesRef.current = [];
    try {
      await streamQAgentMessage(
        chatId,
        requestData,
        (chunk) => {
          streamingContentRef.current += chunk;
          setStreamingContent(streamingContentRef.current);
        },
        (citations) => {
          pendingCitationsRef.current = citations;
        },
        (error) => {
          if (abortController.signal.aborted) return;
          toast.error(error);
          setIsStreaming(false);
          setStreamingContent('');
          streamingContentRef.current = '';
          setActiveToolStatus(null);
          messageQueueRef.current = [];
          setQueuedMessages([]);
          setFailedMessage(content); // Store for retry
        },
        async () => {
          const finalContent = streamingContentRef.current;
          const aiMessageId = `ai-${Date.now()}`;
          if (finalContent) {
            setMessages(prev => [
              ...prev,
              {
                id: aiMessageId,
                content: finalContent,
                type: 'ai',
                citations: pendingCitationsRef.current.length > 0
                  ? pendingCitationsRef.current
                  : undefined,
                notices: pendingNoticesRef.current.length > 0
                  ? pendingNoticesRef.current
                  : undefined,
              } as QAgentMessage,
            ]);
            pendingCitationsRef.current = [];
            pendingNoticesRef.current = [];
          }
          setIsStreaming(false);
          setStreamingContent('');
          streamingContentRef.current = '';
          setActiveToolStatus(null);

          // Focus the input so user can type immediately
          setTimeout(() => mentionInputRef.current?.focus(), 100);

          // Scroll to bottom after AI message renders
          setTimeout(() => scrollToBottom(), 100);

          // Fetch suggestions after AI completes (may have created new ones)
          // Only move the anchor if the AI actually created new suggestions
          {
            const prevSuggestionIds = new Set(suggestions.map(s => s.id));
            const response = await getQAgentSuggestions(chatId);
            if (response.success && response.data) {
              const activeSuggestions = response.data.filter(s => !s.exportedAt);
              setSuggestions(activeSuggestions);
              const newSuggestions = activeSuggestions.filter(s => !prevSuggestionIds.has(s.id));
              if (newSuggestions.length > 0) {
                setSuggestionsAnchorMessageId(aiMessageId);
                // Auto-open the document preview for the reviewed file, scroll
                // to the first new suggestion, and expand the suggestions
                // section in the context panel
                const reviewedFile = projectFiles.find(f => f.id === newSuggestions[0].fileId);
                if (reviewedFile) {
                  setPlaybookToView(null);
                  setFileToView(reviewedFile);
                  setScrollToSuggestionId(newSuggestions[0].id);
                }
                setSuggestionsExpanded(true);
                // Scroll again after suggestions render
                setTimeout(() => scrollToBottom(), 200);
              }
            }
          }

          // Fetch updated chat title
          if (!chatTitle && chatId) {
            setTimeout(async () => {
              try {
                const chatResponse = await getQAgentChat(chatId);
                if (chatResponse.success && chatResponse.data?.title) {
                  setChatTitle(chatResponse.data.title);
                  updateQAgentChatTitle(chatId, chatResponse.data.title);
                }
              } catch (e) {
                // Ignore
              }
            }, 1500);
          }

          // Dequeue next message if any are waiting
          if (messageQueueRef.current.length > 0) {
            const nextMessage = messageQueueRef.current.shift()!;
            setTimeout(() => sendMessageRef.current?.(nextMessage), 100);
          }
        },
        abortController.signal,
        (toolName, status) => {
          setActiveToolStatus(status === 'start' ? toolName : null);
        },
        (notice) => {
          pendingNoticesRef.current = [...pendingNoticesRef.current, notice];
        },
      );
    } catch (error) {
      if (abortController.signal.aborted) return;
      toast.error(t.qagentChat.toasts.sendMessageFailed);
      setIsStreaming(false);
      setStreamingContent('');
      streamingContentRef.current = '';
    }
  };

  sendMessageRef.current = sendMessage;

  const handleSend = useCallback(() => {
    const text = messageTextRef.current;
    if (!text.trim()) return;

    if (isStreaming) {
      // Queue the message - it will be sent after the current stream completes
      messageQueueRef.current.push(text);
      // Show a queued user bubble at the bottom (rendered separately, below streaming)
      const displayContent = mentionsRef.current.reduce((t, m) => {
        return t.replace(`[[${m.type}:${m.id}]]`, `@[${m.name}]`);
      }, text.trim());
      setQueuedMessages(prev => [...prev, {
        id: `queued-${Date.now()}`,
        content: displayContent,
        type: 'human',
      }]);
      setMessageText('');
      messageTextRef.current = '';
      mentionInputRef.current?.clear();
      setTimeout(() => scrollToBottom(), 50);
      return;
    }

    sendMessageRef.current?.(text);
  }, [isStreaming]);


  // Escape key stops streaming or cancels pending skill action
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && pendingSkillAction) {
        e.preventDefault();
        setPendingSkillAction(null);
        return;
      }
      if (e.key === 'Escape' && isStreaming) {
        e.preventDefault();
        handleStop();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isStreaming, pendingSkillAction]);

  // Keyboard navigation for skill file picker
  const reviewSelectedFileIdRef = useRef(reviewSelectedFileId);
  reviewSelectedFileIdRef.current = reviewSelectedFileId;
  const compareSelectedFileIdsRef = useRef(compareSelectedFileIds);
  compareSelectedFileIdsRef.current = compareSelectedFileIds;
  const handleStartSkillRef = useRef(handleStartSkill);
  handleStartSkillRef.current = handleStartSkill;

  useEffect(() => {
    if (!pendingSkillAction) return;

    const readyFiles = projectFiles.filter(f => f.status === 'ready');
    const showPlaybooks = pendingSkillAction === 'review';
    const totalItems = readyFiles.length + (showPlaybooks ? projectPlaybooks.length : 0);
    if (totalItems === 0) return;

    const handlePickerKeyDown = (e: KeyboardEvent) => {
      const idx = reviewPickerIndexRef.current;
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          {
            const next = (idx + 1) % totalItems;
            reviewPickerIndexRef.current = next;
            setReviewPickerIndex(next);
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          {
            const next = (idx - 1 + totalItems) % totalItems;
            reviewPickerIndexRef.current = next;
            setReviewPickerIndex(next);
          }
          break;
        case 'Enter':
          e.preventDefault();
          if (idx < readyFiles.length) {
            const file = readyFiles[idx];
            if (pendingSkillAction === 'compare') {
              // Toggle file in compare multi-select
              setCompareSelectedFileIds(prev => {
                if (prev.includes(file.id)) return prev.filter(id => id !== file.id);
                if (prev.length >= 2) return [prev[1], file.id];
                return [...prev, file.id];
              });
            } else if (reviewSelectedFileIdRef.current === file.id) {
              handleStartSkillRef.current();
            } else {
              setReviewSelectedFileId(file.id);
            }
          } else if (showPlaybooks) {
            const pbIdx = idx - readyFiles.length;
            const pb = projectPlaybooks[pbIdx];
            if (pb) {
              setReviewSelectedPlaybookId(prev => prev === pb.playbookId ? null : pb.playbookId);
            }
          }
          break;
      }
    };
    document.addEventListener('keydown', handlePickerKeyDown);
    return () => document.removeEventListener('keydown', handlePickerKeyDown);
  }, [pendingSkillAction, projectFiles, projectPlaybooks]);

  // Stop streaming: abort the current request and keep partial response
  const handleStop = useCallback(() => {
    abortControllerRef.current?.abort();
    const partialContent = streamingContentRef.current;
    if (partialContent) {
      setMessages(prev => [...prev, {
        id: `ai-${Date.now()}`,
        content: partialContent,
        type: 'ai',
        createdAt: new Date().toISOString(),
      } as QAgentMessage]);
    }
    setIsStreaming(false);
    setStreamingContent('');
    streamingContentRef.current = '';
    setActiveToolStatus(null);
    // Clear the queue - user stopped intentionally
    messageQueueRef.current = [];
    setQueuedMessages([]);
  }, []);

  const isEmptyChat = messages.length === 0 && !isStreaming && !hasStartedChat;

  // --- Word export (QUERI-808) ---
  const [isExportingChat, setIsExportingChat] = useState(false);

  const runDocxExport = async (entries: ExportEntry[], withLabels: boolean) => {
    const { exportToDocx, docxFileName } = await import('@/lib/export-docx');
    const title = chatTitle || projectName || t.qagentChat.export.defaultTitle;
    const now = new Date();
    const date = new Intl.DateTimeFormat(locale === 'sv' ? 'sv-SE' : 'en-GB', { dateStyle: 'long' }).format(now);
    const userName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
    try {
      await exportToDocx({
        title,
        entries,
        dateLine: t.qagentChat.export.dateLine(date),
        labels: withLabels
          ? { user: userName || t.qagentChat.export.youLabel, assistant: 'QAgent' }
          : undefined,
        fileName: docxFileName(title, now),
      });
    } catch (err) {
      console.error('Word export failed', err);
      toast.error(t.qagentChat.export.failed);
    }
  };

  const handleExportAnswer = (markdown: string) =>
    runDocxExport([{ role: 'assistant', content: markdown }], false);

  const handleExportConversation = async () => {
    const entries: ExportEntry[] = messages
      .filter((m) => !m.id.startsWith('queued-'))
      .map((m): ExportEntry => m.type === 'human'
        ? { role: 'user', content: m.content.trim() }
        : { role: 'assistant', content: stripToolResults(m.content) })
      .filter((e) => e.content);
    if (entries.length === 0) return;
    setIsExportingChat(true);
    try {
      await runDocxExport(entries, true);
    } finally {
      setIsExportingChat(false);
    }
  };
  const greeting = user?.firstName ? t.qagentChat.greeting.welcome(user.firstName) : t.qagentChat.greeting.hello;
  // Show context panel whenever a project is selected (not just after chat starts)
  const showContextPanel = !!projectId;

  // Input box
  const InputBox = (
    <div className="relative">
      {/* Retry banner after failed message */}
      {failedMessage && !isStreaming && (
        <div className="flex items-center gap-2 mb-2 px-3 py-2 rounded-lg bg-destructive/10 border border-destructive/20 text-sm">
          <span className="text-destructive text-xs flex-1">{t.qagentChat.input.failed}</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={() => {
              const msg = failedMessage;
              setFailedMessage(null);
              sendMessageRef.current?.(msg);
            }}
          >
            <RotateCcw className="h-3 w-3 mr-1" />
            {t.common.actions.retry}
          </Button>
          <button
            onClick={() => setFailedMessage(null)}
            className="p-0.5 rounded hover:bg-destructive/10 text-destructive/60 hover:text-destructive"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* Project context indicator + clear context */}
      {projectId && projectName && (
        <div className="flex items-center justify-between gap-2 mb-1.5 px-1">
          <button
            onClick={showContextPanel ? handleToggleContextPanel : undefined}
            title={projectName}
            className="inline-flex min-w-0 items-center gap-1.5 px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <FolderOpen className="h-3 w-3 shrink-0" />
            <span className="truncate">{projectName}</span>
            <span className="shrink-0 text-muted-foreground/60">· {t.qagentChat.projectIndicator.fileCount(projectFiles.length)}</span>
          </button>
          {(mentions.length > 0 || messages.length > 0) && (
            <button
              onClick={handleClearConversation}
              className="text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors px-1"
              title={t.qagentChat.projectIndicator.clearTitle}
            >
              {t.qagentChat.projectIndicator.clear}
            </button>
          )}
        </div>
      )}

      {projectId && (
        <Hint id="tip-mention" className="mb-1.5" title={t.onboarding.tips.mention.title}>
          {t.onboarding.tips.mention.body}
        </Hint>
      )}
      <div data-tour="chat-input" className={`relative flex gap-3 items-end border shadow-sm p-2 ${isSlashMenuOpen || isMentionMenuOpen || pendingSkillAction ? 'rounded-b-2xl rounded-t-none border-border border-t-0' : 'rounded-2xl border-border'} ${pendingSkillAction ? 'bg-muted/40' : 'bg-background'}`}>
        <div className="flex-1 flex items-center">
          {pendingSkillAction ? (
            <div className="flex items-center gap-1.5 px-3 py-2 w-full">
              <span className="text-sm font-medium text-muted-foreground select-none">/{pendingSkillAction}</span>
              {reviewSelectedFileId ? (
                <span className="inline-flex items-center gap-1 bg-brand-light text-brand rounded-md px-1.5 py-0.5 text-[0.85em] font-medium border border-brand/20">
                  <FileText className="h-3 w-3" />
                  {projectFiles.find(f => f.id === reviewSelectedFileId)?.name || t.qagentChat.picker.fileChipFallback}
                </span>
              ) : (
                <span className="text-sm text-muted-foreground/50 select-none">{t.qagentChat.picker.selectFilePrompt}</span>
              )}
            </div>
          ) : (
            <MentionInput
              ref={mentionInputRef}
              mentions={mentions}
              onMentionsChange={handleMentionsChange}
              onTextChange={handleMentionTextChange}
              onAtTrigger={handleAtTrigger}
              onAtRemoved={handleAtRemoved}
              onSubmit={handleSend}
              suppressSubmit={isMentionMenuOpen || isSlashMenuOpen}
              onSlashTrigger={handleSlashTrigger}
              onSlashRemoved={handleSlashRemoved}
              placeholder={projectId ? t.qagentChat.input.placeholderWithMentions : t.qagentChat.input.placeholderNoMentions}
            />
          )}
        </div>
        <Button
          size="icon"
          variant="outline"
          onClick={handleSend}
          disabled={!messageText.trim()}
          aria-label={t.qagentChat.input.sendAriaLabel}
          title={t.qagentChat.input.sendTitle}
          className="h-10 w-10 shrink-0 rounded-xl bg-muted/50 border-brand text-brand hover:bg-brand-light hover:text-brand disabled:bg-muted disabled:border-muted disabled:text-muted-foreground"
        >
          <Send className="h-4 w-4" />
        </Button>

        {/* Slash Command Menu - inside the input box so it's glued on top */}
        <SlashCommandMenu
          isOpen={isSlashMenuOpen}
          onClose={() => { setIsSlashMenuOpen(false); setSlashFilter(''); }}
          commands={slashCommands}
          filter={slashFilter}
          onSelect={handleSlashCommandSelect}
          hasProject={!!projectId}
          hasFiles={projectFiles.filter(f => f.status === 'ready').length > 0}
        />

        {/* Mention Menu - positioned above the input box, same pattern as slash commands */}
        <MentionMenu
          isOpen={isMentionMenuOpen}
          onClose={() => { setIsMentionMenuOpen(false); setPendingSkillAction(null); }}
          position={mentionMenuPosition}
          files={projectFiles}
          playbooks={projectPlaybooks}
          onSelectFile={(fileId) => handleMentionSelect('file', fileId)}
          onSelectPlaybook={(playbookId) => handleMentionSelect('playbook', playbookId)}
        />

        {/* Review picker - files + optional playbook, glued above input */}
        {pendingSkillAction && (() => {
          const readyFiles = projectFiles.filter(f => f.status === 'ready');
          const isCompare = pendingSkillAction === 'compare';
          const showPlaybooks = pendingSkillAction === 'review';
          const pickerTitle = {
            review: t.qagentChat.picker.titles.review,
            compare: t.qagentChat.picker.titles.compare,
            summarize: t.qagentChat.picker.titles.summarize,
            'redline-summary': t.qagentChat.picker.titles['redline-summary'],
          }[pendingSkillAction] || t.qagentChat.picker.titles.default;
          // Set together with pendingSkillAction, so it is current on this render
          const workflowTitle = activeWorkflowRef.current?.name;
          const compareChosen = compareSelectedFileIds.length + (compareStandardId ? 1 : 0);
          const isReady = isCompare
            ? compareChosen === 2 && !isPreparingCompare
            : !!reviewSelectedFileId;
          const buttonLabel = isCompare
            ? (compareChosen === 2 ? (compareStandardId ? t.qagentChat.picker.compareWithStandard : t.qagentChat.picker.startComparison) : t.qagentChat.picker.selectFiles(2 - compareChosen))
            : (reviewSelectedFileId ? (pendingSkillAction === 'review' ? t.qagentChat.picker.startReview : pendingSkillAction === 'summarize' ? t.qagentChat.picker.startSummary : t.qagentChat.picker.startAnalysis) : t.qagentChat.picker.selectFileToContinue);

          const handleFileClick = (fileId: string) => {
            if (isCompare) {
              setCompareSelectedFileIds(prev => {
                if (prev.includes(fileId)) return prev.filter(id => id !== fileId);
                // With a standard agreement chosen, one file is compared to it
                if (compareStandardId) return [fileId];
                if (prev.length >= 2) return [prev[1], fileId]; // Replace oldest
                return [...prev, fileId];
              });
            } else {
              setReviewSelectedFileId(prev => prev === fileId ? null : fileId);
            }
          };

          const isFileSelected = (fileId: string) =>
            isCompare ? compareSelectedFileIds.includes(fileId) : reviewSelectedFileId === fileId;

          return (
            <div
              className="absolute z-50 left-0 right-0"
              style={{ bottom: '100%' }}
            >
              <div className="bg-popover border border-border border-b-0 rounded-t-xl overflow-hidden">
                {/* Header */}
                <div className="px-3 py-1.5 border-b bg-muted/30 flex items-center justify-between">
                  <span className="text-sm font-medium">{workflowTitle ? `${workflowTitle} · ${pickerTitle}` : pickerTitle}</span>
                  <button
                    onClick={() => setPendingSkillAction(null)}
                    className="p-0.5 rounded hover:bg-muted text-muted-foreground"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>

                <div className="max-h-[280px] overflow-y-auto">
                  {/* Files section */}
                  <div className="px-3 pt-2 pb-1">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {isCompare ? t.qagentChat.picker.filesSelectTwo : t.qagentChat.picker.file}
                    </span>
                  </div>
                  <div className="py-0.5">
                    {readyFiles.map((file, idx) => (
                      <button
                        key={file.id}
                        onClick={() => handleFileClick(file.id)}
                        onMouseEnter={() => setReviewPickerIndex(idx)}
                        className={cn(
                          'w-full flex items-center gap-2.5 px-3 py-1.5 text-left text-sm transition-colors',
                          'hover:bg-muted focus:outline-none',
                          reviewPickerIndex === idx && 'bg-muted',
                          isFileSelected(file.id) && 'bg-brand/10 text-brand',
                        )}
                      >
                        <FileText className="h-4 w-4 flex-shrink-0" />
                        <span className="truncate flex-1">{file.name}</span>
                        {isFileSelected(file.id) && (
                          <Check className="h-3.5 w-3.5 text-brand flex-shrink-0" />
                        )}
                      </button>
                    ))}
                    {readyFiles.length === 0 && (
                      <div className="px-3 py-3 text-sm text-muted-foreground text-center">
                        {t.qagentChat.picker.noFilesAvailable}
                      </div>
                    )}
                  </div>

                  {/* Standard agreements (compare only): compare a file to our standard */}
                  {isCompare && standardAgreements.length > 0 && (
                    <>
                      <div className="px-3 pt-2 pb-1 border-t">
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t.qagentChat.picker.standardAgreements}</span>
                      </div>
                      <div className="py-0.5">
                        {standardAgreements.map(sa => {
                          const prepStatus = sa.currentVersion?.status;
                          const usable = prepStatus === 'ready';
                          return (
                          <button
                            key={sa.id}
                            disabled={!usable}
                            onClick={() => {
                              setCompareStandardId(prev => (prev === sa.id ? null : sa.id));
                              // One file is compared to the standard: keep the latest
                              setCompareSelectedFileIds(prev => prev.slice(-1));
                            }}
                            className={cn(
                              'w-full flex items-center gap-2.5 px-3 py-1.5 text-left text-sm transition-colors',
                              'hover:bg-muted focus:outline-none disabled:opacity-50 disabled:hover:bg-transparent disabled:cursor-not-allowed',
                              compareStandardId === sa.id && 'bg-brand/10 text-brand',
                            )}
                          >
                            <FileBadge className="h-4 w-4 flex-shrink-0" />
                            <span className="truncate flex-1">{sa.name}</span>
                            {!usable ? (
                              <span className="text-xs text-muted-foreground flex-shrink-0">
                                {prepStatus === 'failed' || !prepStatus ? t.comparisons.prep.failed : t.comparisons.prep.preparing}
                              </span>
                            ) : sa.agreementType && (
                              <span className="text-xs text-muted-foreground flex-shrink-0">{sa.agreementType}</span>
                            )}
                            {compareStandardId === sa.id && (
                              <Check className="h-3.5 w-3.5 text-brand flex-shrink-0" />
                            )}
                          </button>
                          );
                        })}
                      </div>
                    </>
                  )}

                  {/* Playbooks section (only for review skill) */}
                  {showPlaybooks && projectPlaybooks.length > 0 && (
                    <>
                      <div className="px-3 pt-2 pb-1 border-t">
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t.qagentChat.picker.playbook} <span className="normal-case font-normal">{t.qagentChat.picker.optional}</span></span>
                      </div>
                      <div className="py-0.5">
                        {projectPlaybooks.map((pb, idx) => {
                          const pickerIdx = readyFiles.length + idx;
                          return (
                            <button
                              key={pb.playbookId}
                              onClick={() => setReviewSelectedPlaybookId(prev => prev === pb.playbookId ? null : pb.playbookId)}
                              onMouseEnter={() => setReviewPickerIndex(pickerIdx)}
                              className={cn(
                                'w-full flex items-center gap-2.5 px-3 py-1.5 text-left text-sm transition-colors',
                                'hover:bg-muted focus:outline-none',
                                reviewPickerIndex === pickerIdx && 'bg-muted',
                                reviewSelectedPlaybookId === pb.playbookId && 'bg-brand/10 text-brand',
                              )}
                            >
                              <BookOpen className="h-4 w-4 flex-shrink-0" />
                              <span className="truncate flex-1">{pb.playbookName}</span>
                              {reviewSelectedPlaybookId === pb.playbookId && (
                                <Check className="h-3.5 w-3.5 text-brand flex-shrink-0" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>

                {/* Start button */}
                <div className="px-3 py-2 border-t bg-muted/30">
                  <Button
                    size="sm"
                    onClick={() => void handleStartSkill()}
                    disabled={!isReady}
                    className="w-full h-8 text-sm bg-brand text-white hover:bg-brand/90 disabled:opacity-40"
                  >
                    {buttonLabel}
                  </Button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Generate picker is the shared GenerateContractDialog rendered at the bottom of the component tree. */}
      </div>

      {/* ESC to stop hint */}
      {isStreaming && (
        <div className="flex justify-center mt-1.5">
          <span className="text-[11px] text-muted-foreground/50">
            {t.qagentChat.input.escToStopPrefix} <kbd className="px-1 py-0.5 rounded border border-border/50 text-[10px] font-mono">{t.qagentChat.input.escKey}</kbd> {t.qagentChat.input.escToStopSuffix}
          </span>
        </div>
      )}

      {/* MentionMenu moved inside the input box container for correct positioning */}
    </div>
  );

  // Main chat area content - extracted to avoid duplication
  const MainChatArea = (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className={`flex items-center justify-between px-4 py-3 transition-all duration-700 ease-in-out ${hasStartedChat ? 'border-b' : ''}`}>
        <div className="flex items-center gap-3">
          {showContextPanel && (
            <Button
              data-tour="chat-resources"
              variant={isContextPanelOpen ? "ghost" : "outline"}
              size={isContextPanelOpen ? "icon" : "sm"}
              onClick={handleToggleContextPanel}
              className={isContextPanelOpen ? "text-muted-foreground" : "gap-2 text-muted-foreground hover:text-foreground"}
            >
              {isContextPanelOpen ? (
                <PanelLeftClose className="h-5 w-5" />
              ) : (
                <>
                  <FolderOpen className="h-4 w-4" />
                  <span>{t.qagentChat.header.resources}</span>
                </>
              )}
            </Button>
          )}
          <div
            className={`transition-all duration-700 ease-in-out ${
              hasStartedChat ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
            }`}
          >
            <h1 className="font-medium">
              {chatTitle || t.qagentChat.header.newChat}
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {hasStartedChat && messages.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleExportConversation}
              disabled={isStreaming || isExportingChat}
              title={t.qagentChat.header.exportConversationHint}
              className="gap-2 text-muted-foreground hover:text-foreground"
            >
              {isExportingChat ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
              <span className="hidden sm:inline">{t.qagentChat.header.exportConversation}</span>
            </Button>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col relative overflow-hidden">
        {/* Messages area */}
        <div className="relative flex-1 overflow-hidden">
          {/* Welcome text — overlays only the messages area (not the input
              below it), so it can never collide with the project row / tip /
              composer regardless of viewport height or project name length.
              Hidden on very short viewports where it would only be clipped. */}
          <div
            className={`absolute inset-0 flex flex-col items-center justify-center px-4 pb-[8vh] text-center pointer-events-none [@media(max-height:400px)]:hidden transition-all duration-700 ease-in-out ${
              hasStartedChat ? 'opacity-0 -translate-y-16 scale-95' : 'opacity-100 translate-y-0 scale-100'
            }`}
          >
            <h1 className="text-3xl md:text-4xl font-light text-foreground mb-3">
              {greeting}
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground">
              {t.qagentChat.greeting.prompt}
            </p>
          </div>
          <div
            ref={messagesContainerRef}
            onScroll={handleScroll}
            onTouchStart={handleTouchStart}
            className={`h-full overflow-y-auto transition-all duration-700 ease-in-out delay-200 ${
              hasStartedChat ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div className="py-4">
              {loadingMessages ? (
                <div className="flex items-center justify-center h-64">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <div className="w-2 h-2 bg-brand rounded-full animate-pulse" />
                    <span>{t.qagentChat.messages.loading}</span>
                  </div>
                </div>
              ) : (
                <>
                  {chatComparisonId && (
                    <div className="max-w-4xl mx-auto px-4 pb-3">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                        <GitCompare className="h-4 w-4 shrink-0 text-brand" />
                        <span className="min-w-0 flex-1">{t.comparisons.chatBanner}</span>
                        <Link to={`/comparisons/${chatComparisonId}`} className="font-medium text-brand hover:underline">
                          {t.comparisons.chatBannerLink}
                        </Link>
                      </div>
                    </div>
                  )}
                  {messages.map((message, idx) => {
                    // Find the last AI message index for action button visibility
                    let lastAiIdx = -1;
                    for (let i = messages.length - 1; i >= 0; i--) {
                      if (messages[i].type === 'ai' && !messages[i].comparisonId) { lastAiIdx = i; break; }
                    }
                    const isLastAi = message.type === 'ai' && idx === lastAiIdx && !isStreaming;
                    return (
                    <React.Fragment key={message.id}>
                      {/* Contract form message */}
                      {message.content === '__CONTRACT_FORM__' && (message as any)._contractForm ? (
                        <div className="py-3 md:py-4">
                          <div className="max-w-4xl mx-auto px-4">
                            <div className="flex gap-3 items-start">
                              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-brand-light border border-brand/30 -mt-[2px]">
                                <img src={queriIcon} alt="QAgent" className="h-5 w-5" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <ContractFormMessage
                                  parties={(message as any)._contractForm.parties}
                                  otherInfo={(message as any)._contractForm.otherInfo}
                                  onSubmit={handleContractFormSubmit}
                                  disabled={isGenerating}
                                  submitted={(message as any)._contractForm.submitted}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : message.content === '__CONTRACT_FAILED__' && (message as any)._contractFailed ? (
                        <div className="py-3 md:py-4">
                          <div className="max-w-4xl mx-auto px-4">
                            <div className="flex gap-3 items-start">
                              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-brand-light border border-brand/30 -mt-[2px]">
                                <img src={queriIcon} alt="QAgent" className="h-5 w-5" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <ContractFailedMessage
                                  onRetry={() => handleRetryGeneration(message.id, (message as any)._contractFailed.contractId)}
                                  disabled={isGenerating}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : message.content === '__CONTRACT_SECTIONS__' && (message as any)._contractSections ? (
                        <div className="py-3 md:py-4">
                          <div className="max-w-4xl mx-auto px-4">
                            <div className="flex gap-3 items-start">
                              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-brand-light border border-brand/30 -mt-[2px]">
                                <img src={queriIcon} alt="QAgent" className="h-5 w-5" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <ContractSectionsMessage
                                  sections={(message as any)._contractSections.sections}
                                  documentType={(message as any)._contractSections.documentType}
                                  saveStatus={(message as any)._saveStatus}
                                  projectName={(message as any)._projectName}
                                  onOpenDocument={(message as any)._contractFile ? () => setFileToView((message as any)._contractFile) : undefined}
                                  onSave={() => saveContractToProject(message.id, { openViewer: true })}
                                  showAppliedRules={!!(message as any)._contractSections.playbookId}
                                  activeRuleId={(hoveredRule ?? selectedRule)?.messageId === message.id ? (hoveredRule ?? selectedRule)!.ruleId : null}
                                  onRuleHover={(ruleId) => setHoveredRule(ruleId ? { messageId: message.id, ruleId } : null)}
                                  onRuleSelect={(message as any)._contractFile ? (ruleId) => handleRuleSelect(message, ruleId) : undefined}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : message.comparisonId ? (
                        <div className="py-3 md:py-4">
                          <div className="max-w-4xl mx-auto px-4">
                            <div className="flex gap-3 items-start">
                              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-brand-light border border-brand/30 -mt-[2px]">
                                <img src={queriIcon} alt="QAgent" className="h-5 w-5" />
                              </div>
                              <div className="flex-1 min-w-0 max-w-xl">
                                <ComparisonCard comparisonId={message.comparisonId} />
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <QAgentChatMessage
                          message={message}
                          isLastAiMessage={isLastAi}
                          onRegenerate={isLastAi ? handleRegenerate : undefined}
                          onExport={handleExportAnswer}
                        />
                      )}
                      {/* Render suggestions inline after the anchor message */}
                      {message.id === suggestionsAnchorMessageId && !isStreaming && suggestions.filter(s => s.status === 'pending').length > 0 && (
                        <div className="px-4 md:px-6 lg:px-8 py-4">
                          <div className="max-w-3xl mx-auto space-y-3">
                            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                              <span className="font-medium">{t.qagentChat.suggestions.heading}</span>
                              <span className="text-xs bg-brand/10 text-brand px-2 py-0.5 rounded-full">
                                {t.qagentChat.suggestions.pendingCount(suggestions.filter(s => s.status === 'pending').length)}
                              </span>
                              <div className="ml-auto flex items-center gap-1.5">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-6 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
                                  onClick={handleRejectAllSuggestions}
                                >
                                  {t.qagentChat.suggestions.rejectAll}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-6 px-2 text-xs text-brand hover:text-brand hover:bg-brand/10 border-brand/30"
                                  onClick={handleAcceptAllSuggestions}
                                >
                                  {t.qagentChat.suggestions.acceptAll}
                                </Button>
                              </div>
                            </div>
                            {suggestions
                              .filter(s => s.status === 'pending' || (s.status === 'accepted' && !s.exportedAt))
                              .map(suggestion => (
                                <SuggestionCard
                                  key={suggestion.id}
                                  suggestion={suggestion}
                                  onAccept={handleAcceptSuggestion}
                                  onReject={handleRejectSuggestion}
                                  onUndo={handleUndoSuggestion}
                                  onViewInDocument={handleViewSuggestionInDocument}
                                  disabled={isStreaming}
                                />
                              ))}
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );})}
                  {isStreaming && liveContract && (
                    <div className="py-3 md:py-4">
                      <div className="max-w-4xl mx-auto px-4 pl-[3.75rem]">
                        <ContractLivePreview documentType={liveContract.documentType} sections={liveContract.sections} />
                      </div>
                    </div>
                  )}
                  {isStreaming && (
                    <QAgentChatMessage
                      message={{
                        id: 'streaming',
                        content: streamingContent,
                        type: 'ai',
                      }}
                      isStreaming
                      toolStatus={activeToolStatus}
                      skillId={activeSkillId}
                      progress={generationProgress}
                    />
                  )}
                  {queuedMessages.map(qm => (
                    <QAgentChatMessage key={qm.id} message={qm} />
                  ))}
                </>
              )}
            </div>
          </div>

          {/* Scroll to bottom button */}
          {!shouldAutoScroll && hasStartedChat && (
            <div className="absolute bottom-4 right-4">
              <Button
                onClick={scrollToBottom}
                size="icon"
                variant="outline"
                className="h-8 w-8 rounded-full shadow-md bg-background animate-in fade-in-0 slide-in-from-bottom-2"
              >
                <ArrowDown className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Input Area — kept at the bottom in normal flow. (Previously the empty
            chat floated this up with translateY(-35vh) to meet the centered
            greeting, but the project context row + "@" hint made the block taller
            and it overlapped the greeting. Bottom-anchored avoids the collision.) */}
        <div className="w-full p-4 transition-all duration-700 ease-in-out">
          <div className={`mx-auto transition-all duration-700 ${isEmptyChat ? 'max-w-2xl' : 'max-w-3xl'}`}>
            {InputBox}
          </div>
        </div>
      </div>
    </div>
  );

  const hasLeftPanel = showContextPanel && isContextPanelOpen;
  const hasRightPanel = !!fileToView || !!playbookToView;
  const hasPanels = hasLeftPanel || hasRightPanel;

  const ContextPanelContent = (
    <div className="h-full">
      <ContextPanel
        projectId={projectId}
        projectName={projectName}
        files={projectFiles}
        playbooks={projectPlaybooks}
        activeFileIds={activeFileIds}
        activePlaybookId={activePlaybookId}
        onSelectPlaybook={handleSelectPlaybook}
        onUploadClick={handleUploadClick}
        onLinkPlaybookClick={handleOpenLinkDialog}
        onCreatePlaybookClick={() => navigate('/playbooks?new=create')}
        onUploadPlaybookClick={() => navigate('/playbooks?new=import')}
        onDeleteFile={setFileToDelete}
        onViewFile={(file) => {
          setPlaybookToView(null); // Close playbook viewer when opening file
          setFileToView(file);
        }}
        onViewPlaybook={(playbook) => {
          setFileToView(null); // Close file viewer when opening playbook
          setScrollToSuggestionId(null);
          setPlaybookToView(playbook);
        }}
        onUnlinkPlaybook={setPlaybookToUnlink}
        onCompareFile={projectAccess === 'viewer' ? undefined : setCompareFileTarget}
        isLoading={loadingProjectData}
        filesExpanded={filesExpanded}
        playbooksExpanded={playbooksExpanded}
        onFilesExpandedChange={setFilesExpanded}
        onPlaybooksExpandedChange={setPlaybooksExpanded}
        suggestions={suggestions}
        suggestionsExpanded={suggestionsExpanded}
        onSuggestionsExpandedChange={setSuggestionsExpanded}
        onAcceptAllSuggestions={handleAcceptAllSuggestions}
        onRejectAllSuggestions={handleRejectAllSuggestions}
        onViewSuggestion={handleViewSuggestionInDocument}
        onExportFileWithEdits={handleExportFileWithEdits}
        exportingFileId={exportingFileId}
        loadingSuggestions={loadingSuggestions}
      />
    </div>
  );

  const shouldAnimateFileViewer = !fileViewerAnimatedRef.current && !!fileToView;
  if (fileToView && !fileViewerAnimatedRef.current) {
    fileViewerAnimatedRef.current = true;
  }
  if (!fileToView) {
    fileViewerAnimatedRef.current = false;
  }

  const FileViewerContent = (
    <div className={shouldAnimateFileViewer ? 'h-full animate-in slide-in-from-right duration-200' : 'h-full'}>
      <FileViewerSheet
        file={fileToView}
        projectId={fileToView?.projectId || projectId || ''}
        chatId={chatId}
        isOpen={!!fileToView}
        onClose={() => {
          setFileToView(null);
          setScrollToSuggestionId(null);
          setSelectedRule(null);
        }}
        suggestions={suggestions}
        scrollToSuggestionId={scrollToSuggestionId}
        onScrollComplete={() => setScrollToSuggestionId(null)}
        onAcceptSuggestion={handleAcceptSuggestion}
        onRejectSuggestion={handleRejectSuggestion}
        onUndoSuggestion={handleUndoSuggestion}
        onSuggestionsApplyFailed={handleSuggestionsApplyFailed}
        highlightQuotes={ruleHighlightQuotes}
        scrollToQuote={scrollToRuleQuote}
        onQuoteScrollComplete={() => setScrollToRuleQuote(null)}
      />
    </div>
  );

  const shouldAnimatePlaybookViewer = !playbookViewerAnimatedRef.current && !!playbookToView;
  if (playbookToView && !playbookViewerAnimatedRef.current) {
    playbookViewerAnimatedRef.current = true;
  }
  if (!playbookToView) {
    playbookViewerAnimatedRef.current = false;
  }

  const PlaybookViewerContent = (
    <div className={shouldAnimatePlaybookViewer ? 'h-full animate-in slide-in-from-right duration-200' : 'h-full'}>
      <PlaybookViewerSheet
        playbook={playbookToView}
        isOpen={!!playbookToView}
        onClose={() => setPlaybookToView(null)}
      />
    </div>
  );

  const RightPanelContent = fileToView ? FileViewerContent : PlaybookViewerContent;

  return (
    <FileDropZone
      onFilesDropped={handleFilesDropped}
      disabled={!projectId}
      message={projectName ? t.qagentChat.dropZone.withProject(projectName) : t.qagentChat.dropZone.noProject}
    >
    <>
    <div className="h-[calc(100vh-2rem)] bg-background">
      <ResizablePanelGroup orientation="horizontal" className="h-full min-h-0" key={hasLeftPanel ? 'with-ctx' : 'no-ctx'}>
        {/* Context Panel (left) */}
        {hasLeftPanel && (
          <>
            {/* ContextPanel is laid out at min 240px; narrower would clip its row actions */}
            <ResizablePanel defaultSize="20%" minSize="240px" maxSize="100%">
              {ContextPanelContent}
            </ResizablePanel>
            <ResizableHandle withHandle />
          </>
        )}

        {/* Main Chat Area (center) - always present */}
        <ResizablePanel defaultSize={hasRightPanel ? (hasLeftPanel ? "35%" : "50%") : (hasLeftPanel ? "75%" : "100%")} minSize="20%">
          {MainChatArea}
        </ResizablePanel>

        {/* Right Panel (File Viewer or Playbook Viewer) */}
        {hasRightPanel && (
          <>
            <ResizableHandle withHandle />
            <ResizablePanel defaultSize={hasLeftPanel ? "40%" : "50%"} minSize="25%" maxSize="70%">
              {RightPanelContent}
            </ResizablePanel>
          </>
        )}
      </ResizablePanelGroup>
    </div>

      {/* Hidden file input for uploads */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
        accept={PROJECT_FILE_ACCEPT}
        multiple
      />

      {/* Generate contract dialog (shared with QAgentHome and sidebar) */}
      <GenerateContractDialog
        open={isGeneratePickerOpen}
        onOpenChange={setIsGeneratePickerOpen}
        onSubmit={handleStartGenerate}
        isSubmitting={isGenerating}
        initialPlaybookId={activePlaybookId}
      />

      {/* Upload progress dialog */}
      <ProjectUploadDialog open={uploads.isUploading} entries={uploads.entries} />

      {/* Link Playbook Dialog */}
      <Dialog open={isLinkDialogOpen} onOpenChange={setIsLinkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.qagentChat.dialogs.linkPlaybook.title}</DialogTitle>
            <DialogDescription>
              {t.qagentChat.dialogs.linkPlaybook.description}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="playbook">{t.qagentChat.dialogs.linkPlaybook.label}</Label>
              <Select value={selectedPlaybookToLink} onValueChange={setSelectedPlaybookToLink}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t.qagentChat.dialogs.linkPlaybook.placeholder} />
                </SelectTrigger>
                <SelectContent>
                  {availablePlaybooks.length === 0 ? (
                    <div className="px-2 py-3 text-sm text-muted-foreground text-center">
                      {t.qagentChat.dialogs.linkPlaybook.noneAvailable}
                    </div>
                  ) : (
                    availablePlaybooks.map((pb) => (
                      <SelectItem key={pb.id} value={pb.id}>
                        {pb.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsLinkDialogOpen(false);
                setSelectedPlaybookToLink('');
              }}
              disabled={isLinking}
            >
              {t.common.actions.cancel}
            </Button>
            <Button onClick={handleLinkPlaybook} disabled={isLinking || !selectedPlaybookToLink}>
              {isLinking ? t.qagentChat.dialogs.linkPlaybook.linking : t.qagentChat.dialogs.linkPlaybook.submit}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete File Confirmation Dialog */}
      <CompareFileDialog file={compareFileTarget} onClose={() => setCompareFileTarget(null)} />

      <Dialog open={!!fileToDelete} onOpenChange={(open) => !open && setFileToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.qagentChat.dialogs.deleteFile.title}</DialogTitle>
            <DialogDescription>
              {t.qagentChat.dialogs.deleteFile.description(fileToDelete?.name ?? '')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setFileToDelete(null)}
              disabled={isDeletingFile}
            >
              {t.common.actions.cancel}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteFile}
              disabled={isDeletingFile}
            >
              {isDeletingFile ? t.qagentChat.dialogs.deleteFile.deleting : t.common.actions.delete}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unlink Playbook Confirmation Dialog */}
      <Dialog open={!!playbookToUnlink} onOpenChange={(open) => !open && setPlaybookToUnlink(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.qagentChat.dialogs.unlinkPlaybook.title}</DialogTitle>
            <DialogDescription>
              {t.qagentChat.dialogs.unlinkPlaybook.description(playbookToUnlink?.playbookName ?? '')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPlaybookToUnlink(null)}
              disabled={isUnlinkingPlaybook}
            >
              {t.common.actions.cancel}
            </Button>
            <Button
              variant="destructive"
              onClick={handleUnlinkPlaybook}
              disabled={isUnlinkingPlaybook}
            >
              {isUnlinkingPlaybook ? t.qagentChat.dialogs.unlinkPlaybook.unlinking : t.qagentChat.dialogs.unlinkPlaybook.submit}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </>
    </FileDropZone>
  );
};

export default QAgentChat;
